import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import DashboardSidebar from '../../components/layout/DashboardSidebar';
import BlogEditor from '../../components/BlogEditor';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { adminAgencyAPI, consultationAPI, adminBlogAPI, adminTestimonialsAPI, adminJobsAPI, adminAgencyScraperAPI, superAdminAPI, userAPI, courseInquiryAPI, settingsAPI } from '../../lib/api';
import { formatDate, getStatusColor, INDUSTRIES, JOB_TYPES, JOB_ROLES, JOB_ROLE_CATEGORIES, UK_CITIES, EXPERIENCE_LEVELS, WORK_MODES } from '../../lib/utils';
import { toast } from 'sonner';
import { Pagination, PAGE_SIZE } from '../../components/ui/pagination-control';
import { SearchFilterBar } from '../../components/ui/search-filter-bar';
import { JobSourcesPanel } from '../../components/admin/JobSourcesPanel';
import { UpsellStatsPanel } from '../../components/admin/UpsellStatsPanel';
import {
    Building2,
    Plus,
    Edit2,
    Trash2,
    Send,
    Calendar,
    Loader2,
    MapPin,
    BookOpen,
    Eye,
    FileText,
    Zap,
    Star,
    MessageSquareQuote,
    Upload,
    Image,
    Briefcase,
    Search,
    Globe,
    Download,
    CheckCircle2,
    AlertCircle,
    User,
    Target,
    Sparkles,
    History,
    Shield,
    FileCheck,
    RefreshCw,
    Mail,
    Printer,
    Share2,
    Check,
    CreditCard,
    Users,
    Bell,
    FileDown,
    ChevronDown
} from 'lucide-react';

// ============ ADMIN NOTIFICATIONS SECTION ============
const AdminNotificationsSection = () => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [target, setTarget] = useState('individual');
    const [userSearch, setUserSearch] = useState('');
    const [usersList, setUsersList] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [sending, setSending] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    const searchUsers = async (q) => {
        if (!q || q.length < 2) { setUsersList([]); return; }
        setSearchLoading(true);
        try {
            const res = await adminAgencyAPI.listUsersSimple(q);
            setUsersList(res.data.users || []);
        } catch (e) { console.error(e); }
        finally { setSearchLoading(false); }
    };

    const handleSend = async () => {
        if (!title.trim() || !message.trim()) { toast.error('Title and message are required'); return; }
        if (target === 'individual' && !selectedUser) { toast.error('Please select a user'); return; }
        setSending(true);
        try {
            const res = await adminAgencyAPI.sendNotification({
                title: title.trim(),
                message: message.trim(),
                target: target === 'all' ? 'all' : selectedUser.user_id,
                type: 'admin_message'
            });
            toast.success(res.data.message);
            setTitle(''); setMessage(''); setSelectedUser(null); setUserSearch(''); setUsersList([]);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send notification'); }
        finally { setSending(false); }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Send Notification
                </CardTitle>
                <CardDescription>Send notifications to individual users or broadcast to all</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Target Selection */}
                <div className="flex gap-3">
                    <Button
                        variant={target === 'individual' ? 'default' : 'outline'}
                        onClick={() => { setTarget('individual'); setSelectedUser(null); }}
                        data-testid="notif-target-individual"
                    >
                        <User className="w-4 h-4 mr-2" /> Individual User
                    </Button>
                    <Button
                        variant={target === 'all' ? 'default' : 'outline'}
                        onClick={() => setTarget('all')}
                        data-testid="notif-target-all"
                    >
                        <Users className="w-4 h-4 mr-2" /> All Users
                    </Button>
                </div>

                {/* User Search (individual mode) */}
                {target === 'individual' && (
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Select User</label>
                        {selectedUser ? (
                            <div className="flex items-center gap-3 p-3 bg-primary-50 rounded-lg border border-primary-200">
                                <div>
                                    <p className="font-medium text-sm">{selectedUser.name}</p>
                                    <p className="text-xs text-neutral-500">{selectedUser.email}</p>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setUserSearch(''); }} className="ml-auto">
                                    Change
                                </Button>
                            </div>
                        ) : (
                            <div className="relative">
                                <Input
                                    placeholder="Search by name or email..."
                                    value={userSearch}
                                    onChange={(e) => { setUserSearch(e.target.value); searchUsers(e.target.value); }}
                                    data-testid="notif-user-search"
                                />
                                {usersList.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {usersList.map((u) => (
                                            <div
                                                key={u.user_id}
                                                className="px-3 py-2 hover:bg-neutral-50 cursor-pointer flex justify-between items-center"
                                                onClick={() => { setSelectedUser(u); setUsersList([]); setUserSearch(''); }}
                                                data-testid={`notif-user-option-${u.user_id}`}
                                            >
                                                <div>
                                                    <p className="text-sm font-medium">{u.name}</p>
                                                    <p className="text-xs text-neutral-500">{u.email}</p>
                                                </div>
                                                {u.subscription_tier && u.subscription_tier !== 'free' && (
                                                    <Badge variant="outline" className="text-xs capitalize">{u.subscription_tier}</Badge>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {searchLoading && <p className="text-xs text-neutral-500 mt-1">Searching...</p>}
                            </div>
                        )}
                    </div>
                )}

                {target === 'all' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        This will send a notification to all registered users.
                    </div>
                )}

                {/* Notification Content */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Title</label>
                    <Input
                        placeholder="e.g., Multi-Apply Update"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        data-testid="notif-title"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Message</label>
                    <Textarea
                        placeholder="Enter notification message..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        data-testid="notif-message"
                    />
                </div>

                <Button
                    className="bg-primary-600 hover:bg-primary-700"
                    onClick={handleSend}
                    disabled={sending}
                    data-testid="send-notification-btn"
                >
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {target === 'all' ? 'Broadcast to All Users' : 'Send Notification'}
                </Button>
            </CardContent>
        </Card>
    );
};

const AdminDashboard = () => {
    const { isAdmin, isSuperAdmin, user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [agencies, setAgencies] = useState([]);
    const [cvSubmissions, setCvSubmissions] = useState([]);
    const [consultations, setConsultations] = useState([]);
    const [blogPosts, setBlogPosts] = useState([]);
    const [multiApplyOrders, setMultiApplyOrders] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [jobPosts, setJobPosts] = useState([]);
    
    // Live visitors and paid subscribers
    const [liveVisitors, setLiveVisitors] = useState({ active_count: 0, active_users: [] });
    const [paidSubscribers, setPaidSubscribers] = useState([]);
    const [subscribersPage, setSubscribersPage] = useState(1);
    const [subscribersTotal, setSubscribersTotal] = useState(0);
    const [subscribersSearch, setSubscribersSearch] = useState('');
    
    // Pagination state
    const [agenciesPage, setAgenciesPage] = useState(1);
    const [agenciesTotal, setAgenciesTotal] = useState(0);
    const [jobsPage, setJobsPage] = useState(1);
    const [jobsTotal, setJobsTotal] = useState(0);
    const [ordersPage, setOrdersPage] = useState(1);
    const [ordersTotal, setOrdersTotal] = useState(0);
    const [submissionsPage, setSubmissionsPage] = useState(1);
    const [submissionsTotal, setSubmissionsTotal] = useState(0);
    const [consultationsPage, setConsultationsPage] = useState(1);
    const [consultationsTotal, setConsultationsTotal] = useState(0);
    const [blogPage, setBlogPage] = useState(1);
    const [blogTotal, setBlogTotal] = useState(0);
    const [userCvsPage, setUserCvsPage] = useState(1);
    const [userCvsTotal, setUserCvsTotal] = useState(0);
    const [cvReviewsPage, setCvReviewsPage] = useState(1);
    const [cvReviewsTotal, setCvReviewsTotal] = useState(0);
    
    // Search/filter state
    const [agenciesSearch, setAgenciesSearch] = useState('');
    const [agenciesStatus, setAgenciesStatus] = useState('');
    const [jobsSearch, setJobsSearch] = useState('');
    const [ordersStatus, setOrdersStatus] = useState('');
    const [consultationsStatus, setConsultationsStatus] = useState('');
    const [blogSearch, setBlogSearch] = useState('');
    const [blogStatus, setBlogStatus] = useState('');
    
    // Global stats (unfiltered totals for stat cards)
    const [globalStats, setGlobalStats] = useState({ agencies: 0, orders: 0, submissions: 0, consultations: 0 });
    const [showAgencyDialog, setShowAgencyDialog] = useState(false);
    const [showBlogDialog, setShowBlogDialog] = useState(false);
    const [showTestimonialDialog, setShowTestimonialDialog] = useState(false);
    const [showJobDialog, setShowJobDialog] = useState(false);
    const [editingAgency, setEditingAgency] = useState(null);
    const [editingPost, setEditingPost] = useState(null);
    const [editingTestimonial, setEditingTestimonial] = useState(null);
    const [editingJob, setEditingJob] = useState(null);
    const [industrySearch, setIndustrySearch] = useState('');
    const [jobRoleSearch, setJobRoleSearch] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [activeTab, setActiveTab] = useState('agencies');
    const [showBlogEditor, setShowBlogEditor] = useState(false);
    const [editingBlogData, setEditingBlogData] = useState(null);
    
    // Admin Management state (Super Admin only)
    const [adminUsers, setAdminUsers] = useState([]);
    const [adminUsersLoading, setAdminUsersLoading] = useState(false);
    const [showEditAdminDialog, setShowEditAdminDialog] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [editAdminForm, setEditAdminForm] = useState({ email: '', name: '', password: '' });
    const [showCreateAdminDialog, setShowCreateAdminDialog] = useState(false);
    const [createAdminForm, setCreateAdminForm] = useState({ email: '', name: '', password: '', permissions: [] });

    // Staff Management state (Super Admin only)
    const [staffUsers, setStaffUsers] = useState([]);
    const [staffLoading, setStaffLoading] = useState(false);
    const [showCreateStaffDialog, setShowCreateStaffDialog] = useState(false);
    const [createStaffForm, setCreateStaffForm] = useState({ email: '', name: '', password: '' });
    const [showEditStaffDialog, setShowEditStaffDialog] = useState(false);
    const [editingStaff, setEditingStaff] = useState(null);
    const [editStaffForm, setEditStaffForm] = useState({ email: '', name: '', password: '' });

    // Course Inquiries state
    const [courseInquiries, setCourseInquiries] = useState([]);
    const [courseInquiriesLoading, setCourseInquiriesLoading] = useState(false);

    // Site Settings state (WhatsApp)
    const [siteSettings, setSiteSettings] = useState({ whatsapp_number: '', whatsapp_display: '' });
    const [savingSettings, setSavingSettings] = useState(false);
    
    // Job Scraper state
    const [scrapeUrl, setScrapeUrl] = useState('');
    const [scraping, setScraping] = useState(false);
    const [scrapedJobs, setScrapedJobs] = useState([]);
    const [selectedScrapedJobs, setSelectedScrapedJobs] = useState([]);
    const [importAgencyId, setImportAgencyId] = useState('');
    const [importCountry, setImportCountry] = useState('UK');
    const [importing, setImporting] = useState(false);
    
    // Agency Scraper state
    const [agencyScrapeUrl, setAgencyScrapeUrl] = useState('');
    const [agencyScraping, setAgencyScraping] = useState(false);
    const [scrapedAgencies, setScrapedAgencies] = useState([]);
    const [selectedScrapedAgencies, setSelectedScrapedAgencies] = useState([]);
    const [agencyImporting, setAgencyImporting] = useState(false);
    const [editingScrapedAgency, setEditingScrapedAgency] = useState(null);
    
    // Bulk Import state
    const [bulkImporting, setBulkImporting] = useState(false);
    const [bulkImportFile, setBulkImportFile] = useState(null);
    const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
    const [bulkImportCountry, setBulkImportCountry] = useState('UK');

    // Bulk Selection state (for multi-delete)
    const [selectedAgencyIds, setSelectedAgencyIds] = useState([]);
    const [selectedJobIds, setSelectedJobIds] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    
    // Export state
    const [exporting, setExporting] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportCountry, setExportCountry] = useState('all');
    const [exportFormat, setExportFormat] = useState('excel');
    
    // Job Applier state
    const [selectedJobApplierOrder, setSelectedJobApplierOrder] = useState(null);
    const [jobApplierRunning, setJobApplierRunning] = useState(false);
    const [jobApplierLog, setJobApplierLog] = useState([]);
    
    // UCP (Unified Candidate Profile) state
    const [ucpList, setUcpList] = useState([]);
    const [selectedUcp, setSelectedUcp] = useState(null);
    const [ucpLoading, setUcpLoading] = useState(false);
    const [parsingCv, setParsingCv] = useState(false);
    const [ucpHistory, setUcpHistory] = useState(null);
    const [overrideField, setOverrideField] = useState('');
    const [overrideValue, setOverrideValue] = useState('');
    const [overrideReason, setOverrideReason] = useState('');
    
    // User CVs state
    const [usersWithCvs, setUsersWithCvs] = useState([]);
    const [cvUsersLoading, setCvUsersLoading] = useState(false);
    const [downloadingCv, setDownloadingCv] = useState(null);
    
    // CV Reviews state
    const [cvReviews, setCvReviews] = useState([]);
    const [cvReviewsLoading, setCvReviewsLoading] = useState(false);
    const [selectedCvReview, setSelectedCvReview] = useState(null);
    const [copyingShareLink, setCopyingShareLink] = useState(null);
    
    // Google Doc Import state
    const [showGoogleDocDialog, setShowGoogleDocDialog] = useState(false);
    const [googleDocUrl, setGoogleDocUrl] = useState('');
    const [googleDocImporting, setGoogleDocImporting] = useState(false);
    const [googleDocFormData, setGoogleDocFormData] = useState({
        cover_image: '',
        category: 'career-tips',
        tags: [],
        status: 'draft',
    });
    
    const [testimonialFormData, setTestimonialFormData] = useState({
        name: '',
        role: '',
        location: '',
        image_url: '',
        quote: '',
        rating: 5,
        is_active: true,
        display_order: 0,
    });
    const [jobFormData, setJobFormData] = useState({
        agency_id: '',
        title: '',
        description: '',
        location: '',
        job_type: 'full-time',
        salary_min: '',
        salary_max: '',
        salary_period: 'yearly',
        application_deadline: '',
        apply_url: '',
        apply_email: '',
        status: 'active',
    });
    const [blogFormData, setBlogFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        cover_image: '',
        category: 'career-tips',
        tags: [],
        status: 'draft',
    });
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        industry: [],
        job_types: [],
        job_roles: [],
        experience_levels: [],
        work_modes: [],
        address: '',
        city: '',
        operating_cities: [],
        postcode: '',
        latitude: 51.5074,
        longitude: -0.1278,
        email: '',
        phone: '',
        website: '',
        opening_hours: '',
    });
    const [citySearch, setCitySearch] = useState('');

    useEffect(() => {
        // Wait for auth to be initialized before checking
        if (authLoading) {
            return; // Still loading auth state
        }
        if (!isAdmin) {
            navigate('/');
            return;
        }
        fetchData();
    }, [isAdmin, authLoading, navigate]);

    // Effect to fetch data when activeTab changes (for lazy-loaded tabs)
    useEffect(() => {
        if (activeTab === 'cv-reviews' && cvReviews.length === 0) {
            fetchCvReviews();
        }
        if (activeTab === 'user-cvs' && usersWithCvs.length === 0) {
            fetchUsersWithCvs();
        }
        if (activeTab === 'paid-subscribers' && paidSubscribers.length === 0) {
            fetchPaidSubscribers();
        }
        if (activeTab === 'admin-management' && isSuperAdmin && adminUsers.length === 0) {
            fetchAdminUsers();
        }
        if (activeTab === 'staff-management' && isSuperAdmin && staffUsers.length === 0) {
            fetchStaffUsers();
        }
        if (activeTab === 'course-inquiries' && courseInquiries.length === 0) {
            fetchCourseInquiries();
        }
        if (activeTab === 'site-settings' && isSuperAdmin) {
            fetchSiteSettings();
        }
    }, [activeTab]);

    // Auto-refresh live visitors every 30 seconds
    useEffect(() => {
        const interval = setInterval(refreshLiveVisitors, 30000);
        return () => clearInterval(interval);
    }, []);


    const fetchData = async () => {
        setLoading(true);
        try {
            // Use Promise.allSettled to handle partial failures gracefully
            const results = await Promise.allSettled([
                adminAgencyAPI.list({ page: 1, limit: PAGE_SIZE }),
                adminAgencyAPI.getCVSubmissions({ page: 1, limit: PAGE_SIZE }),
                consultationAPI.adminList({ page: 1, limit: PAGE_SIZE }),
                adminBlogAPI.getPosts({ page: 1, limit: PAGE_SIZE }),
                adminAgencyAPI.getMultiApplyOrders({ page: 1, limit: PAGE_SIZE }),
                adminTestimonialsAPI.list(),
                adminJobsAPI.list({ page: 1, limit: PAGE_SIZE }),
            ]);
            
            // Extract data with fallbacks for failed requests
            const [agenciesRes, submissionsRes, consultationsRes, blogRes, multiApplyRes, testimonialsRes, jobsRes] = results;
            
            setAgencies(agenciesRes.status === 'fulfilled' ? agenciesRes.value.data?.agencies || [] : []);
            setAgenciesTotal(agenciesRes.status === 'fulfilled' ? agenciesRes.value.data?.total || 0 : 0);
            setAgenciesPage(1);
            
            setCvSubmissions(submissionsRes.status === 'fulfilled' ? submissionsRes.value.data?.submissions || [] : []);
            setSubmissionsTotal(submissionsRes.status === 'fulfilled' ? submissionsRes.value.data?.total || 0 : 0);
            setSubmissionsPage(1);
            
            setConsultations(consultationsRes.status === 'fulfilled' ? consultationsRes.value.data?.consultations || [] : []);
            setConsultationsTotal(consultationsRes.status === 'fulfilled' ? consultationsRes.value.data?.total || 0 : 0);
            setConsultationsPage(1);
            
            setBlogPosts(blogRes.status === 'fulfilled' ? blogRes.value.data?.posts || [] : []);
            setBlogTotal(blogRes.status === 'fulfilled' ? blogRes.value.data?.total || 0 : 0);
            setBlogPage(1);
            
            setMultiApplyOrders(multiApplyRes.status === 'fulfilled' ? multiApplyRes.value.data?.orders || [] : []);
            setOrdersTotal(multiApplyRes.status === 'fulfilled' ? multiApplyRes.value.data?.total || 0 : 0);
            setOrdersPage(1);
            
            setTestimonials(testimonialsRes.status === 'fulfilled' ? testimonialsRes.value.data?.testimonials || [] : []);
            
            setJobPosts(jobsRes.status === 'fulfilled' ? jobsRes.value.data?.jobs || [] : []);
            setJobsTotal(jobsRes.status === 'fulfilled' ? jobsRes.value.data?.total || 0 : 0);
            setJobsPage(1);
            
            // Store global stats from initial unfiltered load
            setGlobalStats({
                agencies: agenciesRes.status === 'fulfilled' ? agenciesRes.value.data?.total || 0 : 0,
                orders: multiApplyRes.status === 'fulfilled' ? multiApplyRes.value.data?.total || 0 : 0,
                submissions: submissionsRes.status === 'fulfilled' ? submissionsRes.value.data?.total || 0 : 0,
                consultations: consultationsRes.status === 'fulfilled' ? consultationsRes.value.data?.total || 0 : 0,
            });
            
            // Fetch live visitors
            try {
                const liveRes = await adminAgencyAPI.getLiveVisitors();
                setLiveVisitors(liveRes.data);
            } catch (e) {
                console.error('Failed to fetch live visitors:', e);
            }
            
            // Fetch initial paid subscribers count
            try {
                const subsRes = await adminAgencyAPI.getPaidSubscribers({ page: 1, limit: 1 });
                setSubscribersTotal(subsRes.data?.total || 0);
            } catch (e) {
                console.error('Failed to fetch subscribers count:', e);
            }
            
            // Log any failures for debugging
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    console.error(`API call ${index} failed:`, result.reason);
                }
            });
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };
    
    // Individual fetch functions for pagination + search/filter
    const fetchAgencies = async (page, search = agenciesSearch, status = agenciesStatus) => {
        try {
            const params = { page, limit: PAGE_SIZE };
            if (search) params.search = search;
            if (status) params.is_active = status === 'active' ? 'true' : 'false';
            const res = await adminAgencyAPI.list(params);
            setAgencies(res.data?.agencies || []);
            setAgenciesTotal(res.data?.total || 0);
            setAgenciesPage(page);
        } catch (error) {
            toast.error('Failed to load agencies');
        }
    };
    
    const fetchJobs = async (page, search = jobsSearch) => {
        try {
            const params = { page, limit: PAGE_SIZE };
            if (search) params.search = search;
            const res = await adminJobsAPI.list(params);
            setJobPosts(res.data?.jobs || []);
            setJobsTotal(res.data?.total || 0);
            setJobsPage(page);
        } catch (error) {
            toast.error('Failed to load jobs');
        }
    };
    
    const fetchOrders = async (page, status = ordersStatus) => {
        try {
            const params = { page, limit: PAGE_SIZE };
            if (status) params.status = status;
            const res = await adminAgencyAPI.getMultiApplyOrders(params);
            setMultiApplyOrders(res.data?.orders || []);
            setOrdersTotal(res.data?.total || 0);
            setOrdersPage(page);
        } catch (error) {
            toast.error('Failed to load orders');
        }
    };
    
    const fetchSubmissions = async (page) => {
        try {
            const res = await adminAgencyAPI.getCVSubmissions({ page, limit: PAGE_SIZE });
            setCvSubmissions(res.data?.submissions || []);
            setSubmissionsTotal(res.data?.total || 0);
            setSubmissionsPage(page);
        } catch (error) {
            toast.error('Failed to load submissions');
        }
    };
    
    const fetchConsultations = async (page, status = consultationsStatus) => {
        try {
            const params = { page, limit: PAGE_SIZE };
            if (status) params.status = status;
            const res = await consultationAPI.adminList(params);
            setConsultations(res.data?.consultations || []);
            setConsultationsTotal(res.data?.total || 0);
            setConsultationsPage(page);
        } catch (error) {
            toast.error('Failed to load consultations');
        }
    };
    
    const fetchBlogs = async (page, search = blogSearch, status = blogStatus) => {
        try {
            const params = { page, limit: PAGE_SIZE };
            if (search) params.search = search;
            if (status) params.status = status;
            const res = await adminBlogAPI.getPosts(params);
            setBlogPosts(res.data?.posts || []);
            setBlogTotal(res.data?.total || 0);
            setBlogPage(page);
        } catch (error) {
            toast.error('Failed to load blog posts');
        }
    };
    
    const fetchPaidSubscribers = async (page = 1, search = subscribersSearch) => {
        try {
            const params = { page, limit: PAGE_SIZE };
            if (search) params.search = search;
            const res = await adminAgencyAPI.getPaidSubscribers(params);
            setPaidSubscribers(res.data?.subscribers || []);
            setSubscribersTotal(res.data?.total || 0);
            setSubscribersPage(page);
        } catch (error) {
            toast.error('Failed to load subscribers');
        }
    };
    
    const refreshLiveVisitors = async () => {
        try {
            const res = await adminAgencyAPI.getLiveVisitors();
            setLiveVisitors(res.data);
        } catch (e) {
            console.error('Failed to refresh live visitors:', e);
        }
    };

    const handleSubmitJob = async (e) => {
        e.preventDefault();
        try {
            const jobData = {
                ...jobFormData,
                salary_min: jobFormData.salary_min ? parseFloat(jobFormData.salary_min) : null,
                salary_max: jobFormData.salary_max ? parseFloat(jobFormData.salary_max) : null,
            };
            if (editingJob) {
                await adminJobsAPI.update(editingJob.job_id, jobData);
                toast.success('Job post updated');
            } else {
                await adminJobsAPI.create(jobData);
                toast.success('Job post created');
            }
            setShowJobDialog(false);
            setEditingJob(null);
            resetJobForm();
            fetchData();
        } catch (error) {
            console.error('Failed to save job:', error);
            toast.error('Failed to save job post');
        }
    };

    const handleDeleteJob = async (jobId) => {
        if (!window.confirm('Are you sure you want to delete this job post?')) return;
        try {
            await adminJobsAPI.delete(jobId);
            toast.success('Job post deleted');
            fetchData();
        } catch (error) {
            console.error('Failed to delete job:', error);
            toast.error('Failed to delete job post');
        }
    };

    const resetJobForm = () => {
        setJobFormData({
            agency_id: '',
            title: '',
            description: '',
            location: '',
            job_type: 'full-time',
            salary_min: '',
            salary_max: '',
            salary_period: 'yearly',
            application_deadline: '',
            apply_url: '',
            apply_email: '',
            status: 'active',
        });
    };

    // Scraper handlers
    const handleScrape = async () => {
        if (!scrapeUrl.trim()) {
            toast.error('Please enter a URL to scrape');
            return;
        }
        
        setScraping(true);
        setScrapedJobs([]);
        setSelectedScrapedJobs([]);
        
        try {
            const response = await adminJobsAPI.scrape(scrapeUrl);
            const jobs = response.data.jobs || [];
            setScrapedJobs(jobs);
            setSelectedScrapedJobs(jobs.map((_, idx) => idx)); // Select all by default
            
            if (jobs.length === 0) {
                toast.info('No jobs found on this page. Try a different URL.');
            } else {
                toast.success(`Found ${jobs.length} job(s)`);
            }
        } catch (error) {
            console.error('Scraping failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to scrape URL');
        } finally {
            setScraping(false);
        }
    };

    const handleImportJobs = async () => {
        if (selectedScrapedJobs.length === 0) {
            toast.error('Please select at least one job to import');
            return;
        }
        
        setImporting(true);
        
        try {
            const jobsToImport = selectedScrapedJobs.map(idx => scrapedJobs[idx]);
            // Pass agency_id only if one is selected (not 'none' or empty)
            const agencyId = importAgencyId && importAgencyId !== 'none' ? importAgencyId : null;
            const response = await adminJobsAPI.importJobs(agencyId, jobsToImport, importCountry);
            
            toast.success(`Imported ${response.data.imported_count} job(s) into ${importCountry} successfully!`);
            
            // Clear scraper state
            setScrapedJobs([]);
            setSelectedScrapedJobs([]);
            setScrapeUrl('');
            setImportAgencyId('');
            setImportCountry('UK');
            
            // Refresh job posts data
            await fetchData();
            
            // Immediately switch to Job Posts tab to show imported jobs
            setActiveTab('jobs');
        } catch (error) {
            console.error('Import failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to import jobs');
        } finally {
            setImporting(false);
        }
    };

    const toggleJobSelection = (idx) => {
        setSelectedScrapedJobs(prev => 
            prev.includes(idx) 
                ? prev.filter(i => i !== idx)
                : [...prev, idx]
        );
    };

    const selectAllJobs = () => {
        if (selectedScrapedJobs.length === scrapedJobs.length) {
            setSelectedScrapedJobs([]);
        } else {
            setSelectedScrapedJobs(scrapedJobs.map((_, idx) => idx));
        }
    };

    const [editingScrapedJob, setEditingScrapedJob] = useState(null);
    const [editingScrapedJobIdx, setEditingScrapedJobIdx] = useState(null);

    const handleSaveScrapedJob = () => {
        if (editingScrapedJob && editingScrapedJobIdx !== null) {
            const updated = [...scrapedJobs];
            updated[editingScrapedJobIdx] = editingScrapedJob;
            setScrapedJobs(updated);
            setEditingScrapedJob(null);
            setEditingScrapedJobIdx(null);
            toast.success('Job updated');
        }
    };

    // Agency Scraper handlers
    const handleAgencyScrape = async () => {
        if (!agencyScrapeUrl.trim()) {
            toast.error('Please enter a URL to scrape');
            return;
        }
        
        setAgencyScraping(true);
        setScrapedAgencies([]);
        setSelectedScrapedAgencies([]);
        
        try {
            const response = await adminAgencyScraperAPI.scrape(agencyScrapeUrl);
            const agencies = response.data.agencies || [];
            setScrapedAgencies(agencies);
            setSelectedScrapedAgencies(agencies.map((_, idx) => idx)); // Select all by default
            
            if (agencies.length === 0) {
                toast.info('No agencies found on this page. Try a different URL.');
            } else {
                toast.success(`Found ${agencies.length} agency/agencies`);
            }
        } catch (error) {
            console.error('Agency scraping failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to scrape URL');
        } finally {
            setAgencyScraping(false);
        }
    };

    const handleImportAgencies = async () => {
        if (selectedScrapedAgencies.length === 0) {
            toast.error('Please select at least one agency to import');
            return;
        }
        
        setAgencyImporting(true);
        
        try {
            const agenciesToImport = selectedScrapedAgencies.map(idx => scrapedAgencies[idx]);
            const response = await adminAgencyScraperAPI.importAgencies(agenciesToImport);
            
            toast.success(`Imported ${response.data.imported_count} agency/agencies successfully!`);
            
            // Clear scraper state
            setScrapedAgencies([]);
            setSelectedScrapedAgencies([]);
            setAgencyScrapeUrl('');
            
            // Refresh agencies data
            await fetchData();
            
            // Switch to Agencies tab to show imported agencies
            setActiveTab('agencies');
        } catch (error) {
            console.error('Agency import failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to import agencies');
        } finally {
            setAgencyImporting(false);
        }
    };

    const toggleAgencySelection = (idx) => {
        setSelectedScrapedAgencies(prev => 
            prev.includes(idx) 
                ? prev.filter(i => i !== idx)
                : [...prev, idx]
        );
    };

    const selectAllAgencies = () => {
        if (selectedScrapedAgencies.length === scrapedAgencies.length) {
            setSelectedScrapedAgencies([]);
        } else {
            setSelectedScrapedAgencies(scrapedAgencies.map((_, idx) => idx));
        }
    };

    const updateScrapedAgency = (idx, field, value) => {
        setScrapedAgencies(prev => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], [field]: value };
            return updated;
        });
    };

    // Bulk Import: file selection (does NOT trigger upload — user must click Upload)
    const handleBulkImportFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBulkImportFile(file);
    };

    // Bulk Import: actual upload (triggered by Upload button in dialog)
    const handleBulkImport = async () => {
        if (!bulkImportFile) {
            toast.error('Please select a JSON file first');
            return;
        }
        if (!bulkImportCountry) {
            toast.error('Please select a country');
            return;
        }
        
        setBulkImporting(true);
        try {
            const text = await bulkImportFile.text();
            const agencies = JSON.parse(text);
            
            if (!Array.isArray(agencies)) {
                toast.error('Invalid JSON format. Expected an array of agencies.');
                return;
            }
            
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies/bulk-import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                },
                body: JSON.stringify({ agencies, country: bulkImportCountry })
            });
            
            const result = await response.json();
            
            if (response.ok) {
                toast.success(`${result.message} (Country: ${bulkImportCountry})`);
                fetchData(); // Refresh data
                setShowBulkImportDialog(false);
                setBulkImportFile(null);
            } else {
                toast.error(result.detail || 'Import failed');
            }
        } catch (error) {
            toast.error('Failed to parse JSON file: ' + error.message);
        } finally {
            setBulkImporting(false);
        }
    };

    // Export Handlers
    const handleExportAgencies = async (format, exportCountry = 'all') => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (agenciesSearch) params.append('search', agenciesSearch);
            if (agenciesStatus) params.append('is_active', agenciesStatus);
            if (exportCountry && exportCountry !== 'all') params.append('country', exportCountry);
            
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies/export/${format}?${params}`;
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                }
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                const ctySlug = exportCountry && exportCountry !== 'all' ? `_${exportCountry}` : '';
                a.download = `agencies_export${ctySlug}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(downloadUrl);
                const label = exportCountry === 'all' ? 'All Countries' : exportCountry;
                toast.success(`Agencies exported as ${format.toUpperCase()} (${label})`);
                setShowExportDialog(false);
            } else {
                toast.error('Export failed');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export agencies');
        } finally {
            setExporting(false);
        }
    };

    // Job Applier handler
    const handleRunJobApplier = async (orderId, count) => {
        setJobApplierRunning(true);
        const addLog = (message, type = 'info') => {
            const timestamp = new Date().toLocaleTimeString();
            setJobApplierLog(prev => [...prev, { timestamp, message, type }]);
        };
        
        try {
            addLog(`Starting job application process for order ${orderId}...`);
            addLog(`Target: Apply to ${count} Easy Apply jobs`);
            
            // Use UCP-enhanced endpoint if UCP exists
            const endpoint = selectedJobApplierOrder?.ucp_id 
                ? '/api/admin/job-applier/run-with-ucp'
                : '/api/admin/job-applier/run';
            
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                },
                body: JSON.stringify({ order_id: orderId, count })
            });
            
            const result = await response.json();
            
            if (response.ok) {
                addLog(`Successfully applied to ${result.applications_submitted} jobs`, 'success');
                if (result.ucp_version_used) {
                    addLog(`Used UCP version ${result.ucp_version_used} for matching`, 'info');
                }
                if (result.failed_applications > 0) {
                    addLog(`${result.failed_applications} applications failed`, 'error');
                }
                addLog(`Total applications sent: ${result.total_sent}`, 'success');
                toast.success(`Applied to ${result.applications_submitted} jobs`);
                
                fetchData();
            } else {
                addLog(`Error: ${result.detail || 'Unknown error'}`, 'error');
                toast.error(result.detail || 'Failed to run job applier');
            }
        } catch (error) {
            addLog(`Error: ${error.message}`, 'error');
            toast.error('Failed to run job applier');
        } finally {
            setJobApplierRunning(false);
        }
    };

    // User CVs Handlers
    const fetchUsersWithCvs = async (page = 1) => {
        setCvUsersLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users-with-cvs?page=${page}&limit=${PAGE_SIZE}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
            });
            const data = await response.json();
            if (response.ok) {
                setUsersWithCvs(data.users || []);
                setUserCvsTotal(data.total || 0);
                setUserCvsPage(page);
            }
        } catch (error) {
            console.error('Failed to fetch users with CVs:', error);
        } finally {
            setCvUsersLoading(false);
        }
    };

    const handleDownloadUserCv = async (userId, filename) => {
        setDownloadingCv(userId);
        try {
            const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/cv/download/${userId}`;
            const response = await fetch(downloadUrl, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
            });
            
            if (!response.ok) {
                // Try to get error message from response
                let errorMessage = 'Download failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorMessage;
                } catch (e) {
                    // Response might not be JSON
                    if (response.status === 404) {
                        errorMessage = 'CV not found. User may not have uploaded a CV.';
                    }
                }
                throw new Error(errorMessage);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'cv.pdf';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
            toast.success('CV downloaded successfully');
        } catch (error) {
            toast.error(error.message || 'Failed to download CV');
        } finally {
            setDownloadingCv(null);
        }
    };

    // CV Reviews Handlers
    const fetchCvReviews = async (page = 1) => {
        setCvReviewsLoading(true);
        try {
            const skip = (page - 1) * PAGE_SIZE;
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cv-reviews?skip=${skip}&limit=${PAGE_SIZE}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
            });
            const data = await response.json();
            if (response.ok) {
                setCvReviews(data.reviews || []);
                setCvReviewsTotal(data.total || 0);
                setCvReviewsPage(page);
            }
        } catch (error) {
            console.error('Failed to fetch CV reviews:', error);
            toast.error('Failed to fetch CV reviews');
        } finally {
            setCvReviewsLoading(false);
        }
    };

    const handleCopyShareLink = async (shareToken) => {
        const shareUrl = `${window.location.origin}/cv-review/share/${shareToken}`;
        setCopyingShareLink(shareToken);
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Share link copied to clipboard!');
        } catch (error) {
            toast.error('Failed to copy link');
        } finally {
            setTimeout(() => setCopyingShareLink(null), 2000);
        }
    };

    const handleDeleteCvReview = async (reviewId) => {
        if (!window.confirm('Are you sure you want to delete this CV review?')) return;
        
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cv-reviews/${reviewId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
            });
            
            if (response.ok) {
                toast.success('CV review deleted');
                setCvReviews(prev => prev.filter(r => r.review_id !== reviewId));
                if (selectedCvReview?.review_id === reviewId) {
                    setSelectedCvReview(null);
                }
            } else {
                toast.error('Failed to delete CV review');
            }
        } catch (error) {
            toast.error('Failed to delete CV review');
        }
    };

    const handlePrintCvReview = (shareToken) => {
        window.open(`/cv-review/share/${shareToken}`, '_blank');
    };

    // Admin Management Functions (Super Admin only)
    const fetchAdminUsers = async () => {
        if (!isSuperAdmin) return;
        setAdminUsersLoading(true);
        try {
            const response = await superAdminAPI.listAdmins();
            setAdminUsers(response.data.admins || []);
        } catch (error) {
            console.error('Failed to fetch admin users:', error);
            toast.error('Failed to load admin users');
        } finally {
            setAdminUsersLoading(false);
        }
    };

    const handleEditAdmin = (admin) => {
        setEditingAdmin(admin);
        setEditAdminForm({
            email: admin.email || '',
            name: admin.name || '',
            password: '' // Don't pre-fill password
        });
        setShowEditAdminDialog(true);
    };

    const handleUpdateAdmin = async (e) => {
        e.preventDefault();
        if (!editingAdmin) return;
        
        try {
            // Only include fields that have values
            const updateData = {};
            if (editAdminForm.email && editAdminForm.email !== editingAdmin.email) {
                updateData.email = editAdminForm.email;
            }
            if (editAdminForm.name && editAdminForm.name !== editingAdmin.name) {
                updateData.name = editAdminForm.name;
            }
            if (editAdminForm.password) {
                if (editAdminForm.password.length < 8) {
                    toast.error('Password must be at least 8 characters');
                    return;
                }
                updateData.password = editAdminForm.password;
            }
            
            if (Object.keys(updateData).length === 0) {
                toast.info('No changes to save');
                return;
            }
            
            await superAdminAPI.updateAdmin(editingAdmin.user_id, updateData);
            toast.success('Admin updated successfully');
            setShowEditAdminDialog(false);
            setEditingAdmin(null);
            setEditAdminForm({ email: '', name: '', password: '' });
            fetchAdminUsers();
        } catch (error) {
            console.error('Failed to update admin:', error);
            toast.error(error.response?.data?.detail || 'Failed to update admin');
        }
    };

    const handleDeleteAdmin = async (adminUserId, adminEmail) => {
        if (!window.confirm(`Are you sure you want to delete admin "${adminEmail}"? This action cannot be undone.`)) {
            return;
        }
        
        try {
            await superAdminAPI.deleteAdmin(adminUserId);
            toast.success('Admin deleted successfully');
            fetchAdminUsers();
        } catch (error) {
            console.error('Failed to delete admin:', error);
            toast.error(error.response?.data?.detail || 'Failed to delete admin');
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        
        if (!createAdminForm.email || !createAdminForm.name || !createAdminForm.password) {
            toast.error('Please fill in all fields');
            return;
        }
        
        if (createAdminForm.password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        
        try {
            await superAdminAPI.createAdmin(createAdminForm);
            toast.success('Admin created successfully');
            setShowCreateAdminDialog(false);
            setCreateAdminForm({ email: '', name: '', password: '', permissions: [] });
            fetchAdminUsers();
        } catch (error) {
            console.error('Failed to create admin:', error);
            toast.error(error.response?.data?.detail || 'Failed to create admin');
        }
    };

    // ===== Staff Management (Super Admin only) =====
    const fetchStaffUsers = async () => {
        if (!isSuperAdmin) return;
        setStaffLoading(true);
        try {
            const response = await superAdminAPI.listStaff();
            setStaffUsers(response.data.staff || []);
        } catch (error) {
            console.error('Failed to fetch staff:', error);
            toast.error('Failed to load staff accounts');
        } finally {
            setStaffLoading(false);
        }
    };

    const handleCreateStaff = async (e) => {
        e.preventDefault();
        if (!createStaffForm.email || !createStaffForm.name || !createStaffForm.password) {
            toast.error('All fields are required');
            return;
        }
        if (createStaffForm.password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        try {
            await superAdminAPI.createStaff(createStaffForm);
            toast.success('Staff account created');
            setShowCreateStaffDialog(false);
            setCreateStaffForm({ email: '', name: '', password: '' });
            fetchStaffUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create staff');
        }
    };

    const handleEditStaff = (staff) => {
        setEditingStaff(staff);
        setEditStaffForm({ email: staff.email, name: staff.name, password: '' });
        setShowEditStaffDialog(true);
    };

    const handleUpdateStaff = async (e) => {
        e.preventDefault();
        if (!editingStaff) return;
        const payload = {};
        if (editStaffForm.name && editStaffForm.name !== editingStaff.name) payload.name = editStaffForm.name;
        if (editStaffForm.email && editStaffForm.email !== editingStaff.email) payload.email = editStaffForm.email;
        if (editStaffForm.password) payload.password = editStaffForm.password;
        if (Object.keys(payload).length === 0) {
            toast.info('No changes to save');
            return;
        }
        try {
            await superAdminAPI.updateStaff(editingStaff.user_id, payload);
            toast.success('Staff updated');
            setShowEditStaffDialog(false);
            setEditingStaff(null);
            setEditStaffForm({ email: '', name: '', password: '' });
            fetchStaffUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update staff');
        }
    };

    const handleDeleteStaff = async (userId, email) => {
        if (!window.confirm(`Delete staff account ${email}?`)) return;
        try {
            await superAdminAPI.deleteStaff(userId);
            toast.success('Staff removed');
            fetchStaffUsers();
        } catch (error) {
            toast.error('Failed to delete staff');
        }
    };

    // ===== Course Inquiries =====
    const fetchCourseInquiries = async () => {
        setCourseInquiriesLoading(true);
        try {
            const response = await courseInquiryAPI.adminList({ limit: 100 });
            setCourseInquiries(response.data.inquiries || []);
        } catch (error) {
            toast.error('Failed to load inquiries');
        } finally {
            setCourseInquiriesLoading(false);
        }
    };

    const handleUpdateInquiryStatus = async (inquiryId, status) => {
        try {
            await courseInquiryAPI.adminUpdate(inquiryId, { status });
            toast.success('Status updated');
            setCourseInquiries(prev => prev.map(i => i.inquiry_id === inquiryId ? { ...i, status } : i));
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    const handleDeleteInquiry = async (inquiryId) => {
        if (!window.confirm('Delete this inquiry?')) return;
        try {
            await courseInquiryAPI.adminDelete(inquiryId);
            toast.success('Inquiry deleted');
            setCourseInquiries(prev => prev.filter(i => i.inquiry_id !== inquiryId));
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    // ===== Site Settings (WhatsApp) =====
    const fetchSiteSettings = async () => {
        try {
            const response = await settingsAPI.getPublic();
            setSiteSettings({
                whatsapp_number: response.data.whatsapp_number || '',
                whatsapp_display: response.data.whatsapp_display || '',
            });
        } catch (error) {
            console.error('Failed to load settings', error);
        }
    };

    const handleSaveWhatsAppSettings = async () => {
        setSavingSettings(true);
        try {
            await settingsAPI.updateWhatsApp(siteSettings);
            toast.success('WhatsApp number saved');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // UCP Handlers
    const fetchUcpList = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ucp/list`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
            });
            const data = await response.json();
            if (response.ok) {
                setUcpList(data.profiles || []);
            }
        } catch (error) {
            console.error('Failed to fetch UCP list:', error);
        }
    };

    const handleParseCv = async (orderId) => {
        setParsingCv(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ucp/parse-cv/${orderId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
            });
            const data = await response.json();
            
            if (response.ok) {
                toast.success(data.message);
                setSelectedUcp(data.profile);
                fetchUcpList();
                fetchData(); // Refresh orders
            } else {
                toast.error(data.detail || 'Failed to parse CV');
            }
        } catch (error) {
            toast.error('Failed to parse CV');
        } finally {
            setParsingCv(false);
        }
    };

    const handleViewUcp = async (orderId) => {
        setUcpLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ucp/${orderId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
            });
            const data = await response.json();
            
            if (response.ok && data.profile) {
                setSelectedUcp(data.profile);
            } else {
                toast.info('No UCP found. Parse CV first.');
            }
        } catch (error) {
            toast.error('Failed to load UCP');
        } finally {
            setUcpLoading(false);
        }
    };

    const handleViewUcpHistory = async (orderId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ucp/${orderId}/history`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
            });
            const data = await response.json();
            
            if (response.ok) {
                setUcpHistory(data);
            }
        } catch (error) {
            toast.error('Failed to load history');
        }
    };

    const handleOverrideField = async (orderId) => {
        if (!overrideField || !overrideValue) {
            toast.error('Please enter field path and value');
            return;
        }
        
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ucp/${orderId}/override`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                },
                body: JSON.stringify({
                    field_path: overrideField,
                    new_value: overrideValue,
                    reason: overrideReason || 'Manual admin override'
                })
            });
            const data = await response.json();
            
            if (response.ok) {
                toast.success('Field overridden successfully');
                setSelectedUcp(data.profile);
                setOverrideField('');
                setOverrideValue('');
                setOverrideReason('');
            } else {
                toast.error(data.detail || 'Failed to override field');
            }
        } catch (error) {
            toast.error('Failed to override field');
        }
    };

    const handleResolveConflict = async (orderId, conflictIndex, resolution, customValue = null) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ucp/${orderId}/resolve-conflict`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                },
                body: JSON.stringify({
                    conflict_index: conflictIndex,
                    resolution: resolution,
                    custom_value: customValue
                })
            });
            const data = await response.json();
            
            if (response.ok) {
                toast.success('Conflict resolved');
                setSelectedUcp(data.profile);
            } else {
                toast.error(data.detail || 'Failed to resolve conflict');
            }
        } catch (error) {
            toast.error('Failed to resolve conflict');
        }
    };

    const handleSubmitAgency = async (e) => {
        e.preventDefault();
        try {
            if (editingAgency) {
                await adminAgencyAPI.update(editingAgency.agency_id, formData);
                toast.success('Agency updated');
            } else {
                await adminAgencyAPI.create(formData);
                toast.success('Agency created');
            }
            setShowAgencyDialog(false);
            setEditingAgency(null);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Failed to save agency:', error);
            toast.error('Failed to save agency');
        }
    };

    const handleDeleteAgency = async (agencyId) => {
        if (!window.confirm('Are you sure you want to delete this agency?')) return;
        try {
            await adminAgencyAPI.delete(agencyId);
            toast.success('Agency deleted');
            fetchData();
        } catch (error) {
            console.error('Failed to delete agency:', error);
            toast.error('Failed to delete agency');
        }
    };

    const handleBulkDeleteAgencies = async () => {
        if (selectedAgencyIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedAgencyIds.length} selected agencies? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const res = await adminAgencyAPI.bulkDelete(selectedAgencyIds);
            toast.success(res.data.message || `Deleted ${selectedAgencyIds.length} agencies`);
            setSelectedAgencyIds([]);
            fetchData();
        } catch (error) {
            console.error('Bulk delete failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to delete agencies');
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleBulkDeleteJobs = async () => {
        if (selectedJobIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedJobIds.length} selected jobs? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const res = await adminJobsAPI.bulkDelete(selectedJobIds);
            toast.success(res.data.message || `Deleted ${selectedJobIds.length} jobs`);
            setSelectedJobIds([]);
            fetchJobs(jobsPage, jobsSearch);
        } catch (error) {
            console.error('Bulk delete failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to delete jobs');
        } finally {
            setBulkDeleting(false);
        }
    };

    const toggleAdminAgencySelection = (agencyId) => {
        setSelectedAgencyIds((prev) =>
            prev.includes(agencyId) ? prev.filter((id) => id !== agencyId) : [...prev, agencyId]
        );
    };

    const toggleAllAdminAgencies = () => {
        if (selectedAgencyIds.length === agencies.length && agencies.length > 0) {
            setSelectedAgencyIds([]);
        } else {
            setSelectedAgencyIds(agencies.map((a) => a.agency_id));
        }
    };

    const toggleAdminJobSelection = (jobId) => {
        setSelectedJobIds((prev) =>
            prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
        );
    };

    const toggleAllAdminJobs = () => {
        if (selectedJobIds.length === jobPosts.length && jobPosts.length > 0) {
            setSelectedJobIds([]);
        } else {
            setSelectedJobIds(jobPosts.map((j) => j.job_id));
        }
    };

    const handleEditAgency = (agency) => {
        setEditingAgency(agency);
        setFormData({
            name: agency.name || '',
            description: agency.description || '',
            industry: agency.industry || [],
            job_types: agency.job_types || [],
            job_roles: agency.job_roles || [],
            experience_levels: agency.experience_levels || [],
            work_modes: agency.work_modes || [],
            address: agency.address || '',
            city: agency.city || '',
            operating_cities: agency.operating_cities || [],
            postcode: agency.postcode || '',
            country: agency.country || 'UK',
            latitude: agency.latitude || 51.5074,
            longitude: agency.longitude || -0.1278,
            email: agency.email || '',
            phone: agency.phone || '',
            website: agency.website || '',
            opening_hours: agency.opening_hours || '',
        });
        setShowAgencyDialog(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            industry: [],
            job_types: [],
            job_roles: [],
            experience_levels: [],
            work_modes: [],
            address: '',
            city: '',
            operating_cities: [],
            postcode: '',
            country: 'UK',
            latitude: 51.5074,
            longitude: -0.1278,
            email: '',
            phone: '',
            website: '',
            opening_hours: '',
        });
        setIndustrySearch('');
        setJobRoleSearch('');
        setCitySearch('');
    };

    // Testimonial handlers
    const handleSubmitTestimonial = async (e) => {
        e.preventDefault();
        try {
            if (editingTestimonial) {
                await adminTestimonialsAPI.update(editingTestimonial.testimonial_id, testimonialFormData);
                toast.success('Testimonial updated');
            } else {
                await adminTestimonialsAPI.create(testimonialFormData);
                toast.success('Testimonial created');
            }
            setShowTestimonialDialog(false);
            setEditingTestimonial(null);
            resetTestimonialForm();
            fetchData();
        } catch (error) {
            console.error('Failed to save testimonial:', error);
            toast.error('Failed to save testimonial');
        }
    };

    const handleDeleteTestimonial = async (testimonialId) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
        try {
            await adminTestimonialsAPI.delete(testimonialId);
            toast.success('Testimonial deleted');
            fetchData();
        } catch (error) {
            console.error('Failed to delete testimonial:', error);
            toast.error('Failed to delete testimonial');
        }
    };

    const resetTestimonialForm = () => {
        setTestimonialFormData({
            name: '',
            role: '',
            location: '',
            image_url: '',
            quote: '',
            rating: 5,
            is_active: true,
            display_order: 0,
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPEG, PNG, and WebP images are allowed');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setUploadingImage(true);
        try {
            const response = await adminTestimonialsAPI.uploadImage(file);
            const imageUrl = response.data.image_url;
            setTestimonialFormData(prev => ({ ...prev, image_url: imageUrl }));
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Image upload failed:', error);
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    if (loading || authLoading) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
                    <p className="mt-4 text-neutral-600">Loading admin panel...</p>
                </div>
            </Layout>
        );
    }

    // If not admin after auth loaded, show message before redirect
    if (!isAdmin) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <p className="text-neutral-600">Redirecting...</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex min-h-screen bg-neutral-50">
                {/* Sidebar */}
                <DashboardSidebar 
                    userRole={isSuperAdmin ? 'super_admin' : 'admin'}
                    activeSection={activeTab}
                    onSectionChange={setActiveTab}
                />
                
                {/* Main Content */}
                <div className="flex-1 overflow-auto w-full lg:w-auto">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-16 lg:pt-8">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
                            <div>
                                <h1 className="font-heading text-2xl font-bold text-neutral-900">
                                    Admin Dashboard
                                </h1>
                                <p className="text-neutral-500">Manage agencies, submissions, and content</p>
                            </div>
                            <Dialog open={showAgencyDialog} onOpenChange={setShowAgencyDialog}>
                                <DialogTrigger asChild>
                                    <Button 
                                        className="bg-primary-600 hover:bg-primary-700"
                                        onClick={() => {
                                            setEditingAgency(null);
                                            resetForm();
                                        }}
                                        data-testid="add-agency-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Agency
                                    </Button>
                                </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>
                                        {editingAgency ? 'Edit Agency' : 'Add New Agency'}
                                    </DialogTitle>
                                    <DialogDescription>
                                        Fill in the agency details below
                                    </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleSubmitAgency} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Name *</label>
                                            <Input
                                                required
                                                value={formData.name}
                                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                                data-testid="agency-name-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Email</label>
                                            <Input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Description</label>
                                        <Textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                                            rows={3}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Industries (select multiple)</label>
                                            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-white min-h-[42px] max-h-[80px] overflow-y-auto">
                                                {formData.industry.map((ind) => (
                                                    <Badge 
                                                        key={ind} 
                                                        variant="secondary"
                                                        className="cursor-pointer hover:bg-red-100"
                                                        onClick={() => setFormData({
                                                            ...formData, 
                                                            industry: formData.industry.filter(i => i !== ind)
                                                        })}
                                                    >
                                                        {ind} ×
                                                    </Badge>
                                                ))}
                                            </div>
                                            <div className="relative mt-2">
                                                <Input
                                                    placeholder="Search industries..."
                                                    value={industrySearch}
                                                    onChange={(e) => setIndustrySearch(e.target.value)}
                                                    className="mb-1"
                                                />
                                                {industrySearch && (
                                                    <div className="absolute z-10 w-full bg-white border rounded-md shadow-lg max-h-[150px] overflow-y-auto">
                                                        {INDUSTRIES
                                                            .filter(ind => !formData.industry.includes(ind))
                                                            .filter(ind => ind.toLowerCase().includes(industrySearch.toLowerCase()))
                                                            .map((ind) => (
                                                                <div
                                                                    key={ind}
                                                                    className="px-3 py-2 cursor-pointer hover:bg-primary-50 text-sm"
                                                                    onClick={() => {
                                                                        setFormData({...formData, industry: [...formData.industry, ind]});
                                                                        setIndustrySearch('');
                                                                    }}
                                                                >
                                                                    {ind}
                                                                </div>
                                                            ))
                                                        }
                                                        {INDUSTRIES.filter(ind => !formData.industry.includes(ind)).filter(ind => ind.toLowerCase().includes(industrySearch.toLowerCase())).length === 0 && (
                                                            <div className="px-3 py-2 text-sm text-neutral-500">No results</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Job Types</label>
                                            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-white min-h-[42px]">
                                                {formData.job_types.map((jt) => (
                                                    <Badge 
                                                        key={jt} 
                                                        variant="secondary"
                                                        className="cursor-pointer hover:bg-red-100"
                                                        onClick={() => setFormData({
                                                            ...formData, 
                                                            job_types: formData.job_types.filter(j => j !== jt)
                                                        })}
                                                    >
                                                        {JOB_TYPES.find(t => t.value === jt)?.label || jt} ×
                                                    </Badge>
                                                ))}
                                            </div>
                                            <div className="flex gap-2 mt-2">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-xs"
                                                    onClick={() => {
                                                        setFormData({...formData, job_types: JOB_TYPES.map(t => t.value)});
                                                    }}
                                                >
                                                    Select All
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-xs text-neutral-500"
                                                    onClick={() => {
                                                        setFormData({...formData, job_types: []});
                                                    }}
                                                >
                                                    Clear
                                                </Button>
                                            </div>
                                            <Select
                                                value=""
                                                onValueChange={(value) => {
                                                    if (!formData.job_types.includes(value)) {
                                                        setFormData({...formData, job_types: [...formData.job_types, value]});
                                                    }
                                                }}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue placeholder="+ Add job type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {JOB_TYPES.filter(jt => !formData.job_types.includes(jt.value)).map((jt) => (
                                                        <SelectItem key={jt.value} value={jt.value}>{jt.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    {/* Job Roles Multi-Select with Search */}
                                    <div>
                                        <label className="text-sm font-medium">Job Roles (select multiple)</label>
                                        <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-white min-h-[42px] max-h-[100px] overflow-y-auto">
                                            {formData.job_roles.map((role) => (
                                                <Badge 
                                                    key={role} 
                                                    variant="secondary"
                                                    className="cursor-pointer hover:bg-red-100"
                                                    onClick={() => setFormData({
                                                        ...formData, 
                                                        job_roles: formData.job_roles.filter(r => r !== role)
                                                    })}
                                                >
                                                    {role} ×
                                                </Badge>
                                            ))}
                                            {formData.job_roles.length === 0 && (
                                                <span className="text-neutral-400 text-sm">No roles selected</span>
                                            )}
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="text-xs text-neutral-500"
                                                onClick={() => setFormData({...formData, job_roles: []})}
                                            >
                                                Clear All
                                            </Button>
                                        </div>
                                        <div className="relative mt-2">
                                            <Input
                                                placeholder="Type job role and press Enter to add..."
                                                value={jobRoleSearch}
                                                onChange={(e) => setJobRoleSearch(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && jobRoleSearch.trim()) {
                                                        e.preventDefault();
                                                        const newRole = jobRoleSearch.trim();
                                                        if (!formData.job_roles.includes(newRole)) {
                                                            setFormData({...formData, job_roles: [...formData.job_roles, newRole]});
                                                        }
                                                        setJobRoleSearch('');
                                                    }
                                                }}
                                            />
                                            {jobRoleSearch && (
                                                <div className="absolute z-10 w-full bg-white border rounded-md shadow-lg max-h-[200px] overflow-y-auto mt-1">
                                                    {/* Option to add custom role */}
                                                    {jobRoleSearch.trim() && !JOB_ROLES.some(r => r.toLowerCase() === jobRoleSearch.toLowerCase()) && (
                                                        <div
                                                            className="px-3 py-2 cursor-pointer hover:bg-emerald-50 text-sm border-b bg-emerald-50/50 font-medium text-emerald-700"
                                                            onClick={() => {
                                                                const newRole = jobRoleSearch.trim();
                                                                if (!formData.job_roles.includes(newRole)) {
                                                                    setFormData({...formData, job_roles: [...formData.job_roles, newRole]});
                                                                }
                                                                setJobRoleSearch('');
                                                            }}
                                                        >
                                                            + Add "{jobRoleSearch.trim()}" as custom role
                                                        </div>
                                                    )}
                                                    {JOB_ROLES
                                                        .filter(role => !formData.job_roles.includes(role))
                                                        .filter(role => role.toLowerCase().includes(jobRoleSearch.toLowerCase()))
                                                        .slice(0, 20)
                                                        .map((role) => (
                                                            <div
                                                                key={role}
                                                                className="px-3 py-2 cursor-pointer hover:bg-primary-50 text-sm"
                                                                onClick={() => {
                                                                    setFormData({...formData, job_roles: [...formData.job_roles, role]});
                                                                    setJobRoleSearch('');
                                                                }}
                                                            >
                                                                {role}
                                                            </div>
                                                        ))
                                                    }
                                                    {JOB_ROLES.filter(role => !formData.job_roles.includes(role)).filter(role => role.toLowerCase().includes(jobRoleSearch.toLowerCase())).length === 0 && !jobRoleSearch.trim() && (
                                                        <div className="px-3 py-2 text-sm text-neutral-500">No results found</div>
                                                    )}
                                                    {JOB_ROLES.filter(role => !formData.job_roles.includes(role)).filter(role => role.toLowerCase().includes(jobRoleSearch.toLowerCase())).length > 20 && (
                                                        <div className="px-3 py-2 text-xs text-neutral-400 border-t">Showing first 20 results. Type more to narrow down.</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-xs text-neutral-500 mt-1">Type any job role and press Enter, or select from suggestions</p>
                                        <Select
                                            value=""
                                            onValueChange={(value) => {
                                                if (!formData.job_roles.includes(value)) {
                                                    setFormData({...formData, job_roles: [...formData.job_roles, value]});
                                                }
                                            }}
                                        >
                                            <SelectTrigger className="mt-2">
                                                <SelectValue placeholder="Or browse by category" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[300px]">
                                                {Object.entries(JOB_ROLE_CATEGORIES).map(([category, roles]) => (
                                                    <div key={category}>
                                                        <div className="px-2 py-1.5 text-xs font-semibold text-neutral-500 bg-neutral-50 sticky top-0">
                                                            {category}
                                                        </div>
                                                        {roles.filter(role => !formData.job_roles.includes(role)).map((role) => (
                                                            <SelectItem key={role} value={role} className="pl-4">{role}</SelectItem>
                                                        ))}
                                                    </div>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Phone</label>
                                        <Input
                                            value={formData.phone}
                                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        />
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Address *</label>
                                            <Input
                                                required
                                                value={formData.address}
                                                onChange={(e) => setFormData({...formData, address: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Main City (HQ) *</label>
                                            <Input
                                                required
                                                value={formData.city}
                                                onChange={(e) => setFormData({...formData, city: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Postcode *</label>
                                            <Input
                                                required
                                                value={formData.postcode}
                                                onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Country *</label>
                                            <Select
                                                value={formData.country || 'UK'}
                                                onValueChange={(value) => setFormData({...formData, country: value})}
                                            >
                                                <SelectTrigger data-testid="agency-country-select">
                                                    <SelectValue placeholder="Select country" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                                                    <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    {/* Operating Cities */}
                                    <div>
                                        <label className="text-sm font-medium">Also Operates In (other cities)</label>
                                        <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-white min-h-[42px] max-h-[80px] overflow-y-auto">
                                            {formData.operating_cities.map((city) => (
                                                <Badge 
                                                    key={city} 
                                                    variant="secondary"
                                                    className="cursor-pointer hover:bg-red-100"
                                                    onClick={() => setFormData({
                                                        ...formData, 
                                                        operating_cities: formData.operating_cities.filter(c => c !== city)
                                                    })}
                                                >
                                                    {city} ×
                                                </Badge>
                                            ))}
                                            {formData.operating_cities.length === 0 && (
                                                <span className="text-neutral-400 text-sm">No additional cities</span>
                                            )}
                                        </div>
                                        <div className="relative mt-2">
                                            <Input
                                                placeholder="Type city name and press Enter, or search..."
                                                value={citySearch}
                                                onChange={(e) => setCitySearch(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && citySearch.trim()) {
                                                        e.preventDefault();
                                                        const newCity = citySearch.trim();
                                                        if (!formData.operating_cities.includes(newCity) && newCity !== formData.city) {
                                                            setFormData({...formData, operating_cities: [...formData.operating_cities, newCity]});
                                                        }
                                                        setCitySearch('');
                                                    }
                                                }}
                                            />
                                            {citySearch && (
                                                <div className="absolute z-10 w-full bg-white border rounded-md shadow-lg max-h-[150px] overflow-y-auto mt-1">
                                                    {/* Option to add custom city */}
                                                    {citySearch.trim() && !UK_CITIES.some(c => c.toLowerCase() === citySearch.toLowerCase()) && (
                                                        <div
                                                            className="px-3 py-2 cursor-pointer hover:bg-emerald-50 text-sm border-b bg-emerald-50/50 font-medium text-emerald-700"
                                                            onClick={() => {
                                                                const newCity = citySearch.trim();
                                                                if (!formData.operating_cities.includes(newCity) && newCity !== formData.city) {
                                                                    setFormData({...formData, operating_cities: [...formData.operating_cities, newCity]});
                                                                }
                                                                setCitySearch('');
                                                            }}
                                                        >
                                                            + Add "{citySearch.trim()}"
                                                        </div>
                                                    )}
                                                    {UK_CITIES
                                                        .filter(c => !formData.operating_cities.includes(c))
                                                        .filter(c => c !== formData.city)
                                                        .filter(c => c.toLowerCase().includes(citySearch.toLowerCase()))
                                                        .slice(0, 15)
                                                        .map((city) => (
                                                            <div
                                                                key={city}
                                                                className="px-3 py-2 cursor-pointer hover:bg-primary-50 text-sm"
                                                                onClick={() => {
                                                                    setFormData({...formData, operating_cities: [...formData.operating_cities, city]});
                                                                    setCitySearch('');
                                                                }}
                                                            >
                                                                {city}
                                                            </div>
                                                        ))
                                                    }
                                                    {UK_CITIES.filter(c => !formData.operating_cities.includes(c)).filter(c => c !== formData.city).filter(c => c.toLowerCase().includes(citySearch.toLowerCase())).length === 0 && !citySearch.trim() && (
                                                        <div className="px-3 py-2 text-sm text-neutral-500">No cities found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-xs text-neutral-500 mt-1">Type any UK city and press Enter, or select from suggestions</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Latitude *</label>
                                            <Input
                                                required
                                                type="number"
                                                step="any"
                                                value={formData.latitude}
                                                onChange={(e) => setFormData({...formData, latitude: parseFloat(e.target.value)})}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Longitude *</label>
                                            <Input
                                                required
                                                type="number"
                                                step="any"
                                                value={formData.longitude}
                                                onChange={(e) => setFormData({...formData, longitude: parseFloat(e.target.value)})}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Website</label>
                                        <Input
                                            value={formData.website}
                                            onChange={(e) => setFormData({...formData, website: e.target.value})}
                                        />
                                    </div>
                                    <DialogFooter>
                                        <Button type="button" variant="outline" onClick={() => setShowAgencyDialog(false)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" className="bg-primary-600 hover:bg-primary-700">
                                            {editingAgency ? 'Update' : 'Create'} Agency
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                        <Card className="border-green-200 bg-green-50/50">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center relative">
                                    <Users className="w-5 h-5 text-green-600" />
                                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500">Live Now</p>
                                    <p className="font-heading text-xl font-bold text-green-700" data-testid="live-visitors-count">{liveVisitors.active_count}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-primary-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500">Agencies</p>
                                    <p className="font-heading text-xl font-bold">{globalStats.agencies}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                    <Zap className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500">Orders</p>
                                    <p className="font-heading text-xl font-bold">{globalStats.orders}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                    <Send className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500">CV Submissions</p>
                                    <p className="font-heading text-xl font-bold">{globalStats.submissions}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                                    <Calendar className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500">Consultations</p>
                                    <p className="font-heading text-xl font-bold">{globalStats.consultations}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-purple-200 bg-purple-50/50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('paid-subscribers')}>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                                    <CreditCard className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500">Paid Subs</p>
                                    <p className="font-heading text-xl font-bold text-purple-700" data-testid="paid-subs-count">{subscribersTotal}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Tabs - Hidden tabs list, controlled by sidebar */}
                    <Tabs value={activeTab} onValueChange={(val) => {
                        setActiveTab(val);
                        // Fetch users with CVs when switching to that tab
                        if (val === 'user-cvs' && usersWithCvs.length === 0) {
                            fetchUsersWithCvs();
                        }
                        // Fetch CV reviews when switching to that tab
                        if (val === 'cv-reviews' && cvReviews.length === 0) {
                            fetchCvReviews();
                        }
                        // Fetch admin users when switching to admin-management tab
                        if (val === 'admin-management' && isSuperAdmin && adminUsers.length === 0) {
                            fetchAdminUsers();
                        }
                    }} className="space-y-6">
                        <TabsList className="hidden">
                            <TabsTrigger value="agencies">Agencies</TabsTrigger>
                            <TabsTrigger value="multi-apply">Multi-Apply Orders</TabsTrigger>
                            <TabsTrigger value="job-applier">Job Applier</TabsTrigger>
                            <TabsTrigger value="ucp">Candidate Profiles</TabsTrigger>
                            <TabsTrigger value="user-cvs">User CVs</TabsTrigger>
                            <TabsTrigger value="cv-reviews">CV Reviews</TabsTrigger>
                            <TabsTrigger value="submissions">CV Submissions</TabsTrigger>
                            <TabsTrigger value="consultations">Consultations</TabsTrigger>
                            <TabsTrigger value="blog">Blog Posts</TabsTrigger>
                            <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
                            <TabsTrigger value="paid-subscribers">Paid Subscribers</TabsTrigger>
                            <TabsTrigger value="jobs">Job Posts</TabsTrigger>
                            <TabsTrigger value="scraper">Job Scraper</TabsTrigger>
                            <TabsTrigger value="job-sources" data-testid="tab-job-sources">Auto-Sync</TabsTrigger>
                            <TabsTrigger value="agency-scraper">Agency Scraper</TabsTrigger>
                            <TabsTrigger value="course-inquiries" data-testid="tab-course-inquiries">Course Inquiries</TabsTrigger>
                            {isSuperAdmin && <TabsTrigger value="admin-management">Admin Management</TabsTrigger>}
                            {isSuperAdmin && <TabsTrigger value="staff-management" data-testid="tab-staff-management">Staff Management</TabsTrigger>}
                            {isSuperAdmin && <TabsTrigger value="site-settings" data-testid="tab-site-settings">Site Settings</TabsTrigger>}
                            <TabsTrigger value="notifications">Notifications</TabsTrigger>
                        </TabsList>

                        <TabsContent value="agencies">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle>Recruitment Agencies ({agenciesTotal})</CardTitle>
                                    <div className="flex gap-2">
                                        {selectedAgencyIds.length > 0 && (
                                            <Button
                                                variant="destructive"
                                                disabled={bulkDeleting}
                                                onClick={handleBulkDeleteAgencies}
                                                data-testid="bulk-delete-agencies-btn"
                                            >
                                                {bulkDeleting ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                )}
                                                Delete Selected ({selectedAgencyIds.length})
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline"
                                            disabled={exporting}
                                            onClick={() => setShowExportDialog(true)}
                                            data-testid="open-export-dialog-btn"
                                        >
                                            {exporting ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <FileDown className="w-4 h-4 mr-2" />
                                            )}
                                            {exporting ? 'Exporting...' : 'Export'}
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            disabled={bulkImporting}
                                            onClick={() => setShowBulkImportDialog(true)}
                                            data-testid="open-bulk-import-dialog-btn"
                                        >
                                            {bulkImporting ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Upload className="w-4 h-4 mr-2" />
                                            )}
                                            {bulkImporting ? 'Importing...' : 'Bulk Import JSON'}
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <SearchFilterBar
                                        searchPlaceholder="Search agencies by name or city..."
                                        onSearch={(val) => { setAgenciesSearch(val); fetchAgencies(1, val, agenciesStatus); }}
                                        filters={[
                                            { key: 'status', label: 'Status', options: [
                                                { value: 'active', label: 'Active' },
                                                { value: 'inactive', label: 'Inactive' }
                                            ]}
                                        ]}
                                        filterValues={{ status: agenciesStatus }}
                                        onFilterChange={(key, val) => { setAgenciesStatus(val); fetchAgencies(1, agenciesSearch, val); }}
                                    />
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-12">
                                                    <input
                                                        type="checkbox"
                                                        checked={agencies.length > 0 && selectedAgencyIds.length === agencies.length}
                                                        onChange={toggleAllAdminAgencies}
                                                        className="cursor-pointer w-4 h-4 accent-primary-600"
                                                        data-testid="select-all-agencies-checkbox"
                                                        aria-label="Select all agencies"
                                                    />
                                                </TableHead>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Location</TableHead>
                                                <TableHead>Postcode</TableHead>
                                                <TableHead>Industry</TableHead>
                                                <TableHead>Job Roles</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {agencies.map((agency) => (
                                                <TableRow key={agency.agency_id} className={selectedAgencyIds.includes(agency.agency_id) ? 'bg-primary-50/50' : ''}>
                                                    <TableCell>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedAgencyIds.includes(agency.agency_id)}
                                                            onChange={() => toggleAdminAgencySelection(agency.agency_id)}
                                                            className="cursor-pointer w-4 h-4 accent-primary-600"
                                                            data-testid={`select-agency-checkbox-${agency.agency_id}`}
                                                            aria-label={`Select ${agency.name}`}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="font-medium">{agency.name}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-1">
                                                            <MapPin className="w-3 h-3 text-neutral-400" />
                                                            {agency.city}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-neutral-600">{agency.postcode || '-'}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        {Array.isArray(agency.industry) ? agency.industry.slice(0, 2).map((ind) => (
                                                            <Badge key={ind} variant="secondary" className="mr-1">
                                                                {ind}
                                                            </Badge>
                                                        )) : agency.industry ? (
                                                            <Badge variant="secondary" className="mr-1">
                                                                {typeof agency.industry === 'string' ? agency.industry.split(',')[0]?.trim() : '-'}
                                                            </Badge>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                                                            {Array.isArray(agency.job_roles) ? (
                                                                <>
                                                                    {agency.job_roles.slice(0, 2).map((role) => (
                                                                        <Badge key={role} variant="outline" className="text-xs">
                                                                            {role}
                                                                        </Badge>
                                                                    ))}
                                                                    {agency.job_roles.length > 2 && (
                                                                        <Badge variant="outline" className="text-xs">
                                                                            +{agency.job_roles.length - 2}
                                                                        </Badge>
                                                                    )}
                                                                </>
                                                            ) : agency.job_roles ? (
                                                                <Badge variant="outline" className="text-xs">
                                                                    {typeof agency.job_roles === 'string' ? agency.job_roles.split(',')[0]?.trim() : '-'}
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-sm text-neutral-400">-</span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={agency.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-700'}>
                                                            {agency.is_active ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleEditAgency(agency)}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-red-600 hover:bg-red-50"
                                                                onClick={() => handleDeleteAgency(agency.agency_id)}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <Pagination currentPage={agenciesPage} totalItems={agenciesTotal} onPageChange={fetchAgencies} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Multi-Apply Orders Tab */}
                        <TabsContent value="multi-apply">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Multi-Apply Orders ({ordersTotal})</CardTitle>
                                    <CardDescription>View and manage multi-apply job application orders</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <SearchFilterBar
                                        searchPlaceholder="Search by order ID..."
                                        onSearch={(val) => { fetchOrders(1, ordersStatus); }}
                                        filters={[
                                            { key: 'status', label: 'Payment', options: [
                                                { value: 'paid', label: 'Paid' },
                                                { value: 'pending', label: 'Pending' },
                                                { value: 'free', label: 'Free' },
                                                { value: 'failed', label: 'Failed' }
                                            ]}
                                        ]}
                                        filterValues={{ status: ordersStatus }}
                                        onFilterChange={(key, val) => { setOrdersStatus(val); fetchOrders(1, val); }}
                                    />
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Order ID</TableHead>
                                                <TableHead>User</TableHead>
                                                <TableHead>Plan</TableHead>
                                                <TableHead>Jobs Applied</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {multiApplyOrders.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7} className="text-center py-8 text-neutral-500">
                                                        No multi-apply orders yet
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                multiApplyOrders.map((order) => (
                                                    <TableRow key={order.order_id}>
                                                        <TableCell className="font-mono text-xs">{order.order_id?.slice(0, 12)}...</TableCell>
                                                        <TableCell>
                                                            <div>
                                                                <p className="font-medium">{order.user_name || 'N/A'}</p>
                                                                <p className="text-xs text-neutral-500">{order.user_email}</p>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant={order.package === 'pro_plus' ? 'default' : order.package === 'pro' ? 'secondary' : 'outline'}>
                                                                {order.package?.toUpperCase()}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="font-medium">{order.applications_sent || 0}</span>
                                                            <span className="text-neutral-400"> / {order.applications_count || order.applications_total || '?'}</span>
                                                        </TableCell>
                                                        <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                                                        <TableCell>
                                                            <Badge variant={
                                                                order.status === 'completed' ? 'default' :
                                                                order.status === 'in_progress' ? 'secondary' :
                                                                order.status === 'cancelled' ? 'destructive' : 'outline'
                                                            }>
                                                                {order.status || 'pending'}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setSelectedOrder(order)}
                                                            >
                                                                View Details
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                    <Pagination currentPage={ordersPage} totalItems={ordersTotal} onPageChange={fetchOrders} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Job Applier Tab */}
                        <TabsContent value="job-applier">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Zap className="w-5 h-5 text-amber-500" />
                                        Job Applier Tool
                                    </CardTitle>
                                    <CardDescription>
                                        Automatically apply to Easy Apply jobs for Multi-Apply orders. Select an order and run the job application process.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Order Selection */}
                                    <div className="space-y-4">
                                        <h4 className="font-medium">Select Multi-Apply Order</h4>
                                        <div className="grid gap-3 max-h-[400px] overflow-y-auto">
                                            {multiApplyOrders.filter(o => o.status === 'paid' || o.status === 'processing').length === 0 ? (
                                                <div className="text-center py-8 text-neutral-500">
                                                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-neutral-400" />
                                                    <p>No paid orders available for job applications.</p>
                                                </div>
                                            ) : (
                                                multiApplyOrders.filter(o => o.status === 'paid' || o.status === 'processing').map((order) => (
                                                    <div 
                                                        key={order.order_id}
                                                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                                            selectedJobApplierOrder?.order_id === order.order_id 
                                                                ? 'border-primary-500 bg-primary-50' 
                                                                : 'hover:border-neutral-300'
                                                        }`}
                                                        onClick={() => setSelectedJobApplierOrder(order)}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="font-medium">{order.user_name}</p>
                                                                <p className="text-sm text-neutral-500">{order.user_email}</p>
                                                                <div className="flex gap-2 mt-1">
                                                                    <Badge variant="secondary">{order.package_name}</Badge>
                                                                    <Badge variant="outline">{order.applications_total} applications</Badge>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <Badge className={order.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                                                    {order.status}
                                                                </Badge>
                                                                <p className="text-xs text-neutral-500 mt-1">
                                                                    {order.applications_sent || 0} / {order.applications_total} sent
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {order.job_preferences && (
                                                            <div className="mt-3 pt-3 border-t text-sm">
                                                                <p><strong>Roles:</strong> {order.job_preferences.target_roles?.join(', ') || order.job_preferences.job_titles?.join(', ') || 'Any'}</p>
                                                                <p><strong>Location:</strong> {order.job_preferences.preferred_locations?.join(', ') || order.job_preferences.cities?.join(', ') || 'Any'}</p>
                                                                <p className={order.cv_url ? 'text-emerald-600' : 'text-amber-600'}>
                                                                    <strong>CV:</strong> {order.cv_url ? '✓ Uploaded' : '✗ Not uploaded'}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    {selectedJobApplierOrder && (
                                        <div className="p-4 bg-neutral-50 rounded-lg border space-y-4">
                                            <h4 className="font-medium">Application Actions for: {selectedJobApplierOrder.user_name}</h4>
                                            
                                            {/* CV Status */}
                                            <div className={`p-3 rounded-lg border ${selectedJobApplierOrder.cv_url ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                                                <div className="flex items-center gap-3">
                                                    <FileText className={`w-5 h-5 ${selectedJobApplierOrder.cv_url ? 'text-emerald-600' : 'text-amber-600'}`} />
                                                    <div className="flex-1">
                                                        <p className={`font-medium ${selectedJobApplierOrder.cv_url ? 'text-emerald-900' : 'text-amber-900'}`}>
                                                            {selectedJobApplierOrder.cv_url ? 'CV Uploaded' : 'No CV Uploaded'}
                                                        </p>
                                                        {selectedJobApplierOrder.cv_filename && (
                                                            <p className="text-sm text-emerald-700">{selectedJobApplierOrder.cv_filename}</p>
                                                        )}
                                                        {!selectedJobApplierOrder.cv_url && (
                                                            <p className="text-sm text-amber-700">Applications will be submitted without a CV attached.</p>
                                                        )}
                                                    </div>
                                                    {selectedJobApplierOrder.cv_url && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => {
                                                                const token = localStorage.getItem('session_token');
                                                                const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/cv/download-by-order/${selectedJobApplierOrder.order_id}`;
                                                                
                                                                // Create a temporary link with auth
                                                                fetch(downloadUrl, {
                                                                    headers: { 'Authorization': `Bearer ${token}` }
                                                                })
                                                                .then(response => response.blob())
                                                                .then(blob => {
                                                                    const url = window.URL.createObjectURL(blob);
                                                                    const a = document.createElement('a');
                                                                    a.href = url;
                                                                    a.download = selectedJobApplierOrder.cv_filename || 'cv.pdf';
                                                                    document.body.appendChild(a);
                                                                    a.click();
                                                                    window.URL.revokeObjectURL(url);
                                                                    a.remove();
                                                                    toast.success('CV downloaded successfully');
                                                                })
                                                                .catch(() => toast.error('Failed to download CV'));
                                                            }}
                                                            className="text-primary-600 hover:text-primary-700"
                                                        >
                                                            <Download className="w-4 h-4 mr-1" />
                                                            Download CV
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="p-3 bg-white rounded border">
                                                    <p className="text-sm text-neutral-500">Applications Sent</p>
                                                    <p className="text-2xl font-bold text-primary-600">
                                                        {selectedJobApplierOrder.applications_sent || 0}
                                                    </p>
                                                </div>
                                                <div className="p-3 bg-white rounded border">
                                                    <p className="text-sm text-neutral-500">Remaining</p>
                                                    <p className="text-2xl font-bold text-amber-600">
                                                        {selectedJobApplierOrder.applications_total - (selectedJobApplierOrder.applications_sent || 0)}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex gap-3">
                                                <Button 
                                                    className="flex-1 bg-primary-600 hover:bg-primary-700"
                                                    onClick={() => handleRunJobApplier(selectedJobApplierOrder.order_id, 10)}
                                                    disabled={jobApplierRunning}
                                                >
                                                    {jobApplierRunning ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Applying...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Zap className="w-4 h-4 mr-2" />
                                                            Apply to 10 Jobs
                                                        </>
                                                    )}
                                                </Button>
                                                <Button 
                                                    variant="outline"
                                                    onClick={() => handleRunJobApplier(selectedJobApplierOrder.order_id, 50)}
                                                    disabled={jobApplierRunning}
                                                >
                                                    Apply to 50 Jobs
                                                </Button>
                                            </div>

                                            <p className="text-xs text-neutral-500">
                                                * This tool will search for Easy Apply jobs matching the user's preferences and submit applications automatically.
                                            </p>
                                        </div>
                                    )}

                                    {/* Application Log */}
                                    {jobApplierLog.length > 0 && (
                                        <div className="space-y-2">
                                            <h4 className="font-medium">Application Log</h4>
                                            <div className="max-h-[300px] overflow-y-auto border rounded-lg p-3 bg-neutral-900 text-neutral-100 font-mono text-sm">
                                                {jobApplierLog.map((log, idx) => (
                                                    <div key={idx} className={`py-1 ${log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-emerald-400' : 'text-neutral-300'}`}>
                                                        [{log.timestamp}] {log.message}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* UCP (Unified Candidate Profile) Tab */}
                        <TabsContent value="ucp">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Orders List with Parse CV option */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <User className="w-5 h-5" />
                                            Multi-Apply Orders
                                        </CardTitle>
                                        <CardDescription>
                                            Select an order to view or create Unified Candidate Profile
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                            {multiApplyOrders.filter(o => o.status === 'paid' || o.status === 'processing' || o.status === 'completed').map((order) => (
                                                <div 
                                                    key={order.order_id}
                                                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                                        selectedUcp?.order_id === order.order_id 
                                                            ? 'border-primary-500 bg-primary-50' 
                                                            : 'hover:border-neutral-300'
                                                    }`}
                                                    onClick={() => handleViewUcp(order.order_id)}
                                                >
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div>
                                                            <p className="font-medium">{order.user_name}</p>
                                                            <p className="text-sm text-neutral-500">{order.user_email}</p>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            {order.cv_parsed ? (
                                                                <Badge className="bg-emerald-100 text-emerald-700">UCP Ready</Badge>
                                                            ) : order.cv_url ? (
                                                                <Badge variant="outline">CV Uploaded</Badge>
                                                            ) : (
                                                                <Badge variant="secondary">No CV</Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 mt-2">
                                                        {order.cv_url && !order.cv_parsed && (
                                                            <Button
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleParseCv(order.order_id);
                                                                }}
                                                                disabled={parsingCv}
                                                            >
                                                                {parsingCv ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Parse CV & Create UCP'}
                                                            </Button>
                                                        )}
                                                        {order.cv_parsed && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleParseCv(order.order_id);
                                                                }}
                                                                disabled={parsingCv}
                                                            >
                                                                Re-parse CV
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* UCP Details */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <FileText className="w-5 h-5" />
                                            Unified Candidate Profile
                                        </CardTitle>
                                        {selectedUcp && (
                                            <div className="flex gap-2 mt-2">
                                                <Badge variant="outline">v{selectedUcp.version}</Badge>
                                                <Badge className={selectedUcp.overall_confidence > 70 ? 'bg-emerald-100 text-emerald-700' : selectedUcp.overall_confidence > 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                                    {selectedUcp.overall_confidence}% Confidence
                                                </Badge>
                                            </div>
                                        )}
                                    </CardHeader>
                                    <CardContent>
                                        {ucpLoading ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                                            </div>
                                        ) : selectedUcp ? (
                                            <div className="space-y-6">
                                                {/* Personal Info */}
                                                <div>
                                                    <h4 className="font-medium mb-2 flex items-center gap-2">
                                                        <User className="w-4 h-4" />
                                                        Personal Information
                                                        <Badge variant="outline" className="ml-auto text-xs">
                                                            {selectedUcp.confidence_scores?.personal_info || 0}%
                                                        </Badge>
                                                    </h4>
                                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                                        <div><span className="text-neutral-500">Name:</span> {selectedUcp.personal_info?.name || '-'}</div>
                                                        <div><span className="text-neutral-500">Email:</span> {selectedUcp.personal_info?.email || '-'}</div>
                                                        <div><span className="text-neutral-500">Phone:</span> {selectedUcp.personal_info?.phone || '-'}</div>
                                                        <div><span className="text-neutral-500">Location:</span> {selectedUcp.personal_info?.location || '-'}</div>
                                                    </div>
                                                </div>

                                                {/* Skills */}
                                                <div>
                                                    <h4 className="font-medium mb-2 flex items-center gap-2">
                                                        <Sparkles className="w-4 h-4" />
                                                        Skills ({selectedUcp.skills?.count || 0})
                                                        <Badge variant="outline" className="ml-auto text-xs">
                                                            {selectedUcp.confidence_scores?.skills || 0}%
                                                        </Badge>
                                                    </h4>
                                                    <div className="flex flex-wrap gap-1">
                                                        {Array.isArray(selectedUcp.skills?.all) && selectedUcp.skills.all.slice(0, 15).map((skill, idx) => (
                                                            <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                                                        ))}
                                                        {selectedUcp.skills?.count > 15 && (
                                                            <Badge variant="outline" className="text-xs">+{selectedUcp.skills.count - 15} more</Badge>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Experience */}
                                                <div>
                                                    <h4 className="font-medium mb-2 flex items-center gap-2">
                                                        <Briefcase className="w-4 h-4" />
                                                        Experience
                                                        <Badge variant="outline" className="ml-auto text-xs">
                                                            {selectedUcp.confidence_scores?.experience || 0}%
                                                        </Badge>
                                                    </h4>
                                                    <div className="text-sm space-y-1">
                                                        <div><span className="text-neutral-500">Level:</span> <span className="capitalize">{selectedUcp.experience?.level || selectedUcp.job_preferences?.experience_level || '-'}</span></div>
                                                        <div><span className="text-neutral-500">Years:</span> {selectedUcp.experience?.years_estimated || '-'}</div>
                                                        {Array.isArray(selectedUcp.experience?.positions) && selectedUcp.experience.positions.length > 0 && (
                                                            <div>
                                                                <span className="text-neutral-500">Previous Roles:</span>
                                                                <ul className="list-disc list-inside ml-2">
                                                                    {selectedUcp.experience.positions.slice(0, 3).map((pos, idx) => (
                                                                        <li key={idx} className="text-xs">{pos}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Job Preferences */}
                                                <div>
                                                    <h4 className="font-medium mb-2 flex items-center gap-2">
                                                        <Target className="w-4 h-4" />
                                                        Job Preferences
                                                    </h4>
                                                    <div className="text-sm space-y-1">
                                                        <div><span className="text-neutral-500">Target Roles:</span> {Array.isArray(selectedUcp.job_preferences?.target_roles) ? selectedUcp.job_preferences.target_roles.join(', ') : '-'}</div>
                                                        <div><span className="text-neutral-500">Locations:</span> {Array.isArray(selectedUcp.job_preferences?.preferred_locations) ? selectedUcp.job_preferences.preferred_locations.join(', ') : '-'}</div>
                                                        <div><span className="text-neutral-500">Salary:</span> {selectedUcp.job_preferences?.salary_range || '-'}</div>
                                                        <div><span className="text-neutral-500">Remote:</span> {selectedUcp.job_preferences?.remote_preference ? 'Yes' : 'No'}</div>
                                                    </div>
                                                </div>

                                                {/* Conflicts */}
                                                {Array.isArray(selectedUcp.conflicts) && selectedUcp.conflicts.length > 0 && (
                                                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                                        <h4 className="font-medium mb-2 text-amber-800 flex items-center gap-2">
                                                            <AlertCircle className="w-4 h-4" />
                                                            Conflicts Detected ({selectedUcp.conflicts.length})
                                                        </h4>
                                                        <div className="space-y-2">
                                                            {selectedUcp.conflicts.map((conflict, idx) => (
                                                                <div key={idx} className={`p-2 rounded ${conflict.resolved ? 'bg-emerald-50' : 'bg-white'}`}>
                                                                    <div className="flex items-center justify-between">
                                                                        <div>
                                                                            <p className="font-medium text-sm">{conflict.field}</p>
                                                                            <p className="text-xs text-neutral-500">
                                                                                CV: {JSON.stringify(conflict.cv_value)} | Questionnaire: {JSON.stringify(conflict.questionnaire_value)}
                                                                            </p>
                                                                        </div>
                                                                        {conflict.resolved ? (
                                                                            <Badge className="bg-emerald-100 text-emerald-700">Resolved</Badge>
                                                                        ) : (
                                                                            <div className="flex gap-1">
                                                                                <Button size="sm" variant="outline" onClick={() => handleResolveConflict(selectedUcp.order_id, idx, 'use_cv')}>
                                                                                    Use CV
                                                                                </Button>
                                                                                <Button size="sm" variant="outline" onClick={() => handleResolveConflict(selectedUcp.order_id, idx, 'use_questionnaire')}>
                                                                                    Use Form
                                                                                </Button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Manual Override */}
                                                <div className="p-3 bg-neutral-50 border rounded-lg">
                                                    <h4 className="font-medium mb-2 text-sm">Manual Override</h4>
                                                    <div className="space-y-2">
                                                        <Input
                                                            placeholder="Field path (e.g., personal_info.name)"
                                                            value={overrideField}
                                                            onChange={(e) => setOverrideField(e.target.value)}
                                                            className="text-sm"
                                                        />
                                                        <Input
                                                            placeholder="New value"
                                                            value={overrideValue}
                                                            onChange={(e) => setOverrideValue(e.target.value)}
                                                            className="text-sm"
                                                        />
                                                        <Input
                                                            placeholder="Reason for override"
                                                            value={overrideReason}
                                                            onChange={(e) => setOverrideReason(e.target.value)}
                                                            className="text-sm"
                                                        />
                                                        <Button size="sm" onClick={() => handleOverrideField(selectedUcp.order_id)}>
                                                            Apply Override
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* History Button */}
                                                <Button
                                                    variant="outline"
                                                    className="w-full"
                                                    onClick={() => handleViewUcpHistory(selectedUcp.order_id)}
                                                >
                                                    <History className="w-4 h-4 mr-2" />
                                                    View Version History
                                                </Button>

                                                {/* Override History */}
                                                {Array.isArray(selectedUcp.override_history) && selectedUcp.override_history.length > 0 && (
                                                    <div className="text-xs">
                                                        <h5 className="font-medium mb-1">Recent Overrides:</h5>
                                                        {selectedUcp.override_history.slice(-3).map((override, idx) => (
                                                            <div key={idx} className="p-1 bg-neutral-100 rounded mb-1">
                                                                <span className="font-medium">{override.field}</span>: {JSON.stringify(override.old_value)} → {JSON.stringify(override.new_value)}
                                                                <span className="text-neutral-500 ml-1">({override.reason})</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center py-12 text-neutral-500">
                                                <User className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
                                                <p>Select an order to view its Unified Candidate Profile</p>
                                                <p className="text-sm mt-2">Or parse a CV to create one</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        {/* User CVs Tab */}
                        <TabsContent value="user-cvs">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <div>
                                        <CardTitle>User CVs</CardTitle>
                                        <CardDescription>View and download CVs uploaded by users</CardDescription>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={fetchUsersWithCvs}
                                        disabled={cvUsersLoading}
                                    >
                                        {cvUsersLoading ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : null}
                                        Refresh
                                    </Button>
                                </CardHeader>
                                <CardContent>
                                    {cvUsersLoading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                                        </div>
                                    ) : usersWithCvs.length === 0 ? (
                                        <div className="text-center py-8 text-neutral-500">
                                            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                            <p>No users have uploaded CVs yet</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>User</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Role</TableHead>
                                                    <TableHead>Subscription</TableHead>
                                                    <TableHead>CV File</TableHead>
                                                    <TableHead>Uploaded</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {usersWithCvs.map((user) => (
                                                    <TableRow key={user.user_id}>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                {user.picture ? (
                                                                    <img 
                                                                        src={user.picture} 
                                                                        alt={user.name} 
                                                                        className="w-8 h-8 rounded-full"
                                                                    />
                                                                ) : (
                                                                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                                                        <User className="w-4 h-4 text-primary-600" />
                                                                    </div>
                                                                )}
                                                                <span className="font-medium">{user.name}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-neutral-600">{user.email}</TableCell>
                                                        <TableCell>
                                                            <Badge variant={user.role === 'super_admin' ? 'destructive' : user.role === 'admin' ? 'default' : 'secondary'}>
                                                                {user.role}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant={user.subscription_tier === 'pro' ? 'default' : user.subscription_tier === 'standard' ? 'secondary' : 'outline'}>
                                                                {user.subscription_tier || 'free'}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <FileText className="w-4 h-4 text-emerald-600" />
                                                                <span className="text-sm truncate max-w-[150px]" title={user.cv_filename}>
                                                                    {user.cv_filename || 'CV uploaded'}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-sm text-neutral-500">
                                                            {formatDate(user.updated_at)}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleDownloadUserCv(user.user_id, user.cv_filename)}
                                                                disabled={downloadingCv === user.user_id}
                                                                data-testid={`download-cv-${user.user_id}`}
                                                            >
                                                                {downloadingCv === user.user_id ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                                                ) : (
                                                                    <Download className="w-4 h-4 mr-1" />
                                                                )}
                                                                Download
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                    <Pagination currentPage={userCvsPage} totalItems={userCvsTotal} onPageChange={fetchUsersWithCvs} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* CV Reviews Tab */}
                        <TabsContent value="cv-reviews">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <FileCheck className="w-5 h-5 text-primary-600" />
                                            CV Reviews ({cvReviewsTotal})
                                        </CardTitle>
                                        <p className="text-sm text-neutral-500 mt-1">
                                            View, print, and share CV review results with users
                                        </p>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        onClick={fetchCvReviews}
                                        disabled={cvReviewsLoading}
                                    >
                                        {cvReviewsLoading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="w-4 h-4" />
                                        )}
                                        <span className="ml-2">Refresh</span>
                                    </Button>
                                </CardHeader>
                                <CardContent>
                                    {cvReviewsLoading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                                        </div>
                                    ) : cvReviews.length === 0 ? (
                                        <div className="text-center py-12">
                                            <FileCheck className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                                            <p className="text-neutral-500">No CV reviews yet</p>
                                            <p className="text-sm text-neutral-400">CV reviews will appear here when users analyze their CVs</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Filename</TableHead>
                                                    <TableHead>Score</TableHead>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {cvReviews.map((review) => (
                                                    <TableRow key={review.review_id}>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <Mail className="w-4 h-4 text-neutral-400" />
                                                                {review.email || 'N/A'}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            {review.filename || 'Unknown'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge className={
                                                                review.overall_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                                                review.overall_score >= 60 ? 'bg-amber-100 text-amber-700' :
                                                                'bg-red-100 text-red-700'
                                                            }>
                                                                {review.overall_score}/100
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-neutral-500">
                                                            {review.created_at ? new Date(review.created_at).toLocaleDateString('en-GB', {
                                                                day: 'numeric',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            }) : 'N/A'}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => setSelectedCvReview(review)}
                                                                    title="View Details"
                                                                >
                                                                    <Eye className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handlePrintCvReview(review.share_token)}
                                                                    title="Print / Open in New Tab"
                                                                >
                                                                    <Printer className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleCopyShareLink(review.share_token)}
                                                                    title="Copy Share Link"
                                                                >
                                                                    {copyingShareLink === review.share_token ? (
                                                                        <Check className="w-4 h-4 text-emerald-500" />
                                                                    ) : (
                                                                        <Share2 className="w-4 h-4" />
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteCvReview(review.review_id)}
                                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                    <Pagination currentPage={cvReviewsPage} totalItems={cvReviewsTotal} onPageChange={fetchCvReviews} />
                                </CardContent>
                            </Card>
                            {selectedCvReview && (
                                <Dialog open={!!selectedCvReview} onOpenChange={() => setSelectedCvReview(null)}>
                                    <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                                        <DialogHeader>
                                            <DialogTitle className="flex items-center gap-2">
                                                <FileCheck className="w-5 h-5 text-primary-600" />
                                                CV Review Details
                                            </DialogTitle>
                                        </DialogHeader>
                                        
                                        <div className="space-y-6">
                                            {/* Info Header */}
                                            <div className="flex flex-wrap items-center gap-4 p-4 bg-neutral-50 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <Mail className="w-4 h-4 text-neutral-500" />
                                                    <span className="text-sm">{selectedCvReview.email || 'N/A'}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-neutral-500" />
                                                    <span className="text-sm">{selectedCvReview.filename || 'Unknown'}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-neutral-500" />
                                                    <span className="text-sm">
                                                        {selectedCvReview.created_at ? new Date(selectedCvReview.created_at).toLocaleString() : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Overall Score */}
                                            <div className="text-center p-6 bg-gradient-to-br from-primary-50 to-violet-50 rounded-xl">
                                                <div className="text-5xl font-bold bg-gradient-to-r from-primary-600 to-violet-600 bg-clip-text text-transparent mb-2">
                                                    {selectedCvReview.overall_score}
                                                </div>
                                                <p className="text-neutral-600">Overall ATS Score</p>
                                            </div>

                                            {/* Sections */}
                                            {selectedCvReview.sections && selectedCvReview.sections.length > 0 && (
                                                <div>
                                                    <h4 className="font-semibold mb-3">Section Breakdown</h4>
                                                    <div className="space-y-3">
                                                        {selectedCvReview.sections.map((section, idx) => (
                                                            <div key={idx} className="p-3 border rounded-lg">
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="font-medium">{section.name}</span>
                                                                    <Badge className={
                                                                        section.score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                                                        section.score >= 60 ? 'bg-amber-100 text-amber-700' :
                                                                        'bg-red-100 text-red-700'
                                                                    }>
                                                                        {section.score}/100
                                                                    </Badge>
                                                                </div>
                                                                <p className="text-sm text-neutral-600">{section.feedback}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Keywords */}
                                            {selectedCvReview.keywords && (
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="p-4 bg-emerald-50 rounded-lg">
                                                        <h5 className="font-medium text-emerald-800 mb-2">Keywords Found</h5>
                                                        <div className="flex flex-wrap gap-1">
                                                            {selectedCvReview.keywords.found?.map((kw, i) => (
                                                                <Badge key={i} className="bg-emerald-100 text-emerald-700 text-xs">{kw}</Badge>
                                                            )) || <span className="text-sm text-neutral-500">None</span>}
                                                        </div>
                                                    </div>
                                                    <div className="p-4 bg-amber-50 rounded-lg">
                                                        <h5 className="font-medium text-amber-800 mb-2">Missing Keywords</h5>
                                                        <div className="flex flex-wrap gap-1">
                                                            {selectedCvReview.keywords.missing?.map((kw, i) => (
                                                                <Badge key={i} variant="outline" className="border-amber-300 text-amber-700 text-xs">{kw}</Badge>
                                                            )) || <span className="text-sm text-neutral-500">None</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Recommendations */}
                                            {selectedCvReview.recommendations && selectedCvReview.recommendations.length > 0 && (
                                                <div>
                                                    <h4 className="font-semibold mb-3">Recommendations</h4>
                                                    <ul className="space-y-2">
                                                        {selectedCvReview.recommendations.map((rec, idx) => (
                                                            <li key={idx} className="flex gap-2 text-sm">
                                                                <span className="text-primary-600 font-bold">{idx + 1}.</span>
                                                                {rec}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="flex gap-3 pt-4 border-t">
                                                <Button
                                                    variant="outline"
                                                    onClick={() => handleCopyShareLink(selectedCvReview.share_token)}
                                                    className="flex-1"
                                                >
                                                    <Share2 className="w-4 h-4 mr-2" />
                                                    Copy Share Link
                                                </Button>
                                                <Button
                                                    onClick={() => handlePrintCvReview(selectedCvReview.share_token)}
                                                    className="flex-1 bg-gradient-to-r from-primary-600 to-violet-600"
                                                >
                                                    <Printer className="w-4 h-4 mr-2" />
                                                    Print / View Full Report
                                                </Button>
                                            </div>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            )}
                        </TabsContent>

                        <TabsContent value="submissions">
                            <Card>
                                <CardHeader>
                                    <CardTitle>CV Submissions ({submissionsTotal})</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User</TableHead>
                                                <TableHead>Agency</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {cvSubmissions.map((submission) => (
                                                <TableRow key={submission.submission_id}>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{submission.user_name}</p>
                                                            <p className="text-sm text-neutral-500">{submission.user_email}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{submission.agency_id}</TableCell>
                                                    <TableCell>{formatDate(submission.created_at)}</TableCell>
                                                    <TableCell>
                                                        <Badge className={`${getStatusColor(submission.status)} border capitalize`}>
                                                            {submission.status}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <Pagination currentPage={submissionsPage} totalItems={submissionsTotal} onPageChange={fetchSubmissions} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="consultations">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Consultations ({consultationsTotal})</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <SearchFilterBar
                                        searchPlaceholder="Search consultations..."
                                        onSearch={() => { fetchConsultations(1, consultationsStatus); }}
                                        filters={[
                                            { key: 'status', label: 'Status', options: [
                                                { value: 'pending', label: 'Pending' },
                                                { value: 'confirmed', label: 'Confirmed' },
                                                { value: 'completed', label: 'Completed' },
                                                { value: 'cancelled', label: 'Cancelled' }
                                            ]}
                                        ]}
                                        filterValues={{ status: consultationsStatus }}
                                        onFilterChange={(key, val) => { setConsultationsStatus(val); fetchConsultations(1, val); }}
                                    />
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User</TableHead>
                                                <TableHead>Type</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Duration</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {consultations.map((consultation) => (
                                                <TableRow key={consultation.consultation_id}>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{consultation.user_name}</p>
                                                            <p className="text-sm text-neutral-500">{consultation.user_email}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="capitalize">
                                                        {consultation.consultation_type.replace('_', ' ')}
                                                    </TableCell>
                                                    <TableCell>{formatDate(consultation.scheduled_date)}</TableCell>
                                                    <TableCell>{consultation.duration_minutes} mins</TableCell>
                                                    <TableCell>
                                                        <Badge className={`${getStatusColor(consultation.status)} border capitalize`}>
                                                            {consultation.status}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <Pagination currentPage={consultationsPage} totalItems={consultationsTotal} onPageChange={fetchConsultations} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Blog Posts Tab */}
                        <TabsContent value="blog">
                            {showBlogEditor ? (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-xl font-bold">
                                            {editingBlogData ? 'Edit Blog Post' : 'Create New Blog Post'}
                                        </h2>
                                        <Button 
                                            variant="outline"
                                            onClick={() => {
                                                setShowBlogEditor(false);
                                                setEditingBlogData(null);
                                            }}
                                        >
                                            Back to Posts
                                        </Button>
                                    </div>
                                    <BlogEditor
                                        initialData={editingBlogData}
                                        onSave={async (blogData) => {
                                            try {
                                                if (editingBlogData) {
                                                    await adminBlogAPI.update(editingBlogData.post_id, blogData);
                                                    toast.success('Blog post updated successfully!');
                                                } else {
                                                    await adminBlogAPI.create(blogData);
                                                    toast.success('Blog post created successfully!');
                                                }
                                                setShowBlogEditor(false);
                                                setEditingBlogData(null);
                                                fetchData();
                                            } catch (error) {
                                                console.error('Failed to save blog post:', error);
                                                toast.error(error.response?.data?.detail || 'Failed to save blog post');
                                            }
                                        }}
                                        onCancel={() => {
                                            setShowBlogEditor(false);
                                            setEditingBlogData(null);
                                        }}
                                    />
                                </div>
                            ) : (
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle>Blog Posts ({blogTotal})</CardTitle>
                                    <div className="flex gap-2">
                                        {/* Import from Google Doc Button */}
                                        <Dialog open={showGoogleDocDialog} onOpenChange={setShowGoogleDocDialog}>
                                            <DialogTrigger asChild>
                                                <Button 
                                                    variant="outline"
                                                    onClick={() => {
                                                        setGoogleDocUrl('');
                                                        setGoogleDocFormData({
                                                            cover_image: '',
                                                            category: 'career-tips',
                                                            tags: [],
                                                            status: 'draft',
                                                        });
                                                    }}
                                                >
                                                    <FileText className="w-4 h-4 mr-2" />
                                                    Import from Google Doc
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className="max-w-xl">
                                                <DialogHeader>
                                                    <DialogTitle>Import Blog from Google Doc</DialogTitle>
                                                    <DialogDescription>
                                                        Paste your Google Doc URL to import the blog post with formatting (bold, links, headings) preserved for SEO.
                                                    </DialogDescription>
                                                </DialogHeader>
                                                <div className="space-y-4 py-4">
                                                    <div>
                                                        <label className="text-sm font-medium">Google Doc URL *</label>
                                                        <Input
                                                            value={googleDocUrl}
                                                            onChange={(e) => setGoogleDocUrl(e.target.value)}
                                                            placeholder="https://docs.google.com/document/d/..."
                                                        />
                                                        <p className="text-xs text-neutral-500 mt-1">
                                                            Make sure the document is publicly accessible or shared with "Anyone with the link"
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Cover Image URL (optional)</label>
                                                        <Input
                                                            value={googleDocFormData.cover_image}
                                                            onChange={(e) => setGoogleDocFormData({...googleDocFormData, cover_image: e.target.value})}
                                                            placeholder="https://example.com/image.jpg"
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="text-sm font-medium">Category</label>
                                                            <Select
                                                                value={googleDocFormData.category}
                                                                onValueChange={(value) => setGoogleDocFormData({...googleDocFormData, category: value})}
                                                            >
                                                                <SelectTrigger>
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="career-tips">Career Tips</SelectItem>
                                                                    <SelectItem value="industry-news">Industry News</SelectItem>
                                                                    <SelectItem value="job-search">Job Search</SelectItem>
                                                                    <SelectItem value="interviews">Interviews</SelectItem>
                                                                    <SelectItem value="workplace">Workplace</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Status</label>
                                                            <Select
                                                                value={googleDocFormData.status}
                                                                onValueChange={(value) => setGoogleDocFormData({...googleDocFormData, status: value})}
                                                            >
                                                                <SelectTrigger>
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="draft">Draft</SelectItem>
                                                                    <SelectItem value="published">Published</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Tags (comma-separated)</label>
                                                        <Input
                                                            value={googleDocFormData.tags.join(', ')}
                                                            onChange={(e) => setGoogleDocFormData({
                                                                ...googleDocFormData, 
                                                                tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                                                            })}
                                                            placeholder="remote work, career tips, job search"
                                                        />
                                                    </div>
                                                </div>
                                                <DialogFooter>
                                                    <Button 
                                                        variant="outline" 
                                                        onClick={() => setShowGoogleDocDialog(false)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button 
                                                        onClick={async () => {
                                                            if (!googleDocUrl) {
                                                                toast.error('Please enter a Google Doc URL');
                                                                return;
                                                            }
                                                            setGoogleDocImporting(true);
                                                            try {
                                                                const response = await adminBlogAPI.importGoogleDoc({
                                                                    doc_url: googleDocUrl,
                                                                    ...googleDocFormData
                                                                });
                                                                toast.success('Blog post imported successfully!');
                                                                setShowGoogleDocDialog(false);
                                                                fetchData();
                                                            } catch (error) {
                                                                console.error('Failed to import from Google Doc:', error);
                                                                toast.error(error.response?.data?.detail || 'Failed to import blog post');
                                                            } finally {
                                                                setGoogleDocImporting(false);
                                                            }
                                                        }}
                                                        disabled={googleDocImporting || !googleDocUrl}
                                                        className="bg-primary-600 hover:bg-primary-700"
                                                    >
                                                        {googleDocImporting ? (
                                                            <>
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                Importing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Download className="w-4 h-4 mr-2" />
                                                                Import Blog
                                                            </>
                                                        )}
                                                    </Button>
                                                </DialogFooter>
                                            </DialogContent>
                                        </Dialog>
                                        
                                        {/* New Post Button - Opens BlogEditor */}
                                        <Button 
                                            onClick={() => {
                                                setEditingBlogData(null);
                                                setShowBlogEditor(true);
                                            }}
                                            className="bg-primary-600 hover:bg-primary-700"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            New Post
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <SearchFilterBar
                                        searchPlaceholder="Search blog posts by title..."
                                        onSearch={(val) => { setBlogSearch(val); fetchBlogs(1, val, blogStatus); }}
                                        filters={[
                                            { key: 'status', label: 'Status', options: [
                                                { value: 'draft', label: 'Draft' },
                                                { value: 'published', label: 'Published' }
                                            ]}
                                        ]}
                                        filterValues={{ status: blogStatus }}
                                        onFilterChange={(key, val) => { setBlogStatus(val); fetchBlogs(1, blogSearch, val); }}
                                    />
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Title</TableHead>
                                                <TableHead>Category</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Views</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {blogPosts.map((post) => (
                                                <TableRow key={post.post_id}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                                                                <FileText className="w-5 h-5 text-primary-600" />
                                                            </div>
                                                            <div>
                                                                <p className="font-medium line-clamp-1">{post.title}</p>
                                                                <p className="text-sm text-neutral-500 line-clamp-1">{post.excerpt}</p>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="capitalize">
                                                        {post.category.replace('-', ' ')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                                            {post.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="flex items-center gap-1 text-neutral-500">
                                                            <Eye className="w-4 h-4" />
                                                            {post.views}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>{formatDate(post.created_at)}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setEditingBlogData(post);
                                                                    setShowBlogEditor(true);
                                                                }}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                onClick={async () => {
                                                                    if (window.confirm('Delete this post?')) {
                                                                        try {
                                                                            await adminBlogAPI.delete(post.post_id);
                                                                            toast.success('Post deleted');
                                                                            fetchData();
                                                                        } catch (error) {
                                                                            toast.error('Failed to delete');
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {blogPosts.length === 0 && (
                                        <div className="text-center py-12">
                                            <BookOpen className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                                            <p className="text-neutral-500">No blog posts yet. Create your first post!</p>
                                        </div>
                                    )}
                                    <Pagination currentPage={blogPage} totalItems={blogTotal} onPageChange={fetchBlogs} />
                                </CardContent>
                            </Card>
                            )}
                        </TabsContent>

                        {/* Testimonials Tab */}
                        <TabsContent value="testimonials">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <MessageSquareQuote className="w-5 h-5" />
                                        Success Stories / Testimonials
                                    </CardTitle>
                                    <Dialog open={showTestimonialDialog} onOpenChange={setShowTestimonialDialog}>
                                        <DialogTrigger asChild>
                                            <Button 
                                                className="bg-primary-600 hover:bg-primary-700"
                                                onClick={() => {
                                                    setEditingTestimonial(null);
                                                    resetTestimonialForm();
                                                }}
                                                data-testid="add-testimonial-btn"
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Add Testimonial
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-lg">
                                            <DialogHeader>
                                                <DialogTitle>
                                                    {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
                                                </DialogTitle>
                                                <DialogDescription>
                                                    Add a success story from a job seeker
                                                </DialogDescription>
                                            </DialogHeader>
                                            <form onSubmit={handleSubmitTestimonial} className="space-y-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-sm font-medium">Name *</label>
                                                        <Input
                                                            required
                                                            placeholder="Sarah Thompson"
                                                            value={testimonialFormData.name}
                                                            onChange={(e) => setTestimonialFormData({...testimonialFormData, name: e.target.value})}
                                                            data-testid="testimonial-name-input"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Role *</label>
                                                        <Input
                                                            required
                                                            placeholder="Marketing Manager"
                                                            value={testimonialFormData.role}
                                                            onChange={(e) => setTestimonialFormData({...testimonialFormData, role: e.target.value})}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-sm font-medium">Location *</label>
                                                        <Input
                                                            required
                                                            placeholder="London"
                                                            value={testimonialFormData.location}
                                                            onChange={(e) => setTestimonialFormData({...testimonialFormData, location: e.target.value})}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Rating</label>
                                                        <Select
                                                            value={testimonialFormData.rating.toString()}
                                                            onValueChange={(value) => setTestimonialFormData({...testimonialFormData, rating: parseInt(value)})}
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {[5, 4, 3, 2, 1].map((r) => (
                                                                    <SelectItem key={r} value={r.toString()}>
                                                                        {r} Star{r > 1 ? 's' : ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium">Photo</label>
                                                    <div className="space-y-3">
                                                        {/* Image Preview */}
                                                        {testimonialFormData.image_url && (
                                                            <div className="flex items-center gap-3">
                                                                <img 
                                                                    src={testimonialFormData.image_url.startsWith('/api') 
                                                                        ? `${process.env.REACT_APP_BACKEND_URL}${testimonialFormData.image_url}`
                                                                        : testimonialFormData.image_url
                                                                    }
                                                                    alt="Preview"
                                                                    className="w-16 h-16 rounded-full object-cover border-2 border-neutral-200"
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => setTestimonialFormData({...testimonialFormData, image_url: ''})}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Upload Button */}
                                                        <div className="flex gap-2">
                                                            <label className="flex-1">
                                                                <input
                                                                    type="file"
                                                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                                                    onChange={handleImageUpload}
                                                                    className="hidden"
                                                                    disabled={uploadingImage}
                                                                />
                                                                <div className="flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                                                                    {uploadingImage ? (
                                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <Upload className="w-4 h-4 text-neutral-500" />
                                                                    )}
                                                                    <span className="text-sm text-neutral-600">
                                                                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                                                                    </span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                        
                                                        {/* Or URL Input */}
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-neutral-400">or paste URL:</span>
                                                            <Input
                                                                placeholder="https://example.com/photo.jpg"
                                                                value={testimonialFormData.image_url}
                                                                onChange={(e) => setTestimonialFormData({...testimonialFormData, image_url: e.target.value})}
                                                                className="flex-1 h-8 text-sm"
                                                            />
                                                        </div>
                                                        <p className="text-xs text-neutral-500">Accepts JPEG, PNG, WebP (max 5MB). Leave blank for default avatar.</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium">Quote / Testimonial *</label>
                                                    <Textarea
                                                        required
                                                        placeholder="Share their success story..."
                                                        value={testimonialFormData.quote}
                                                        onChange={(e) => setTestimonialFormData({...testimonialFormData, quote: e.target.value})}
                                                        rows={4}
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-sm font-medium">Display Order</label>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            value={testimonialFormData.display_order}
                                                            onChange={(e) => setTestimonialFormData({...testimonialFormData, display_order: parseInt(e.target.value) || 0})}
                                                        />
                                                        <p className="text-xs text-neutral-500 mt-1">Lower numbers appear first</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 pt-6">
                                                        <input
                                                            type="checkbox"
                                                            id="is_active"
                                                            checked={testimonialFormData.is_active}
                                                            onChange={(e) => setTestimonialFormData({...testimonialFormData, is_active: e.target.checked})}
                                                            className="w-4 h-4"
                                                        />
                                                        <label htmlFor="is_active" className="text-sm">Active (visible on site)</label>
                                                    </div>
                                                </div>
                                                <DialogFooter>
                                                    <Button type="submit" className="bg-primary-600 hover:bg-primary-700">
                                                        {editingTestimonial ? 'Update' : 'Create'} Testimonial
                                                    </Button>
                                                </DialogFooter>
                                            </form>
                                        </DialogContent>
                                    </Dialog>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Person</TableHead>
                                                <TableHead>Quote</TableHead>
                                                <TableHead>Rating</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {testimonials.map((testimonial) => (
                                                <TableRow key={testimonial.testimonial_id}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            {testimonial.image_url ? (
                                                                <img 
                                                                    src={testimonial.image_url.startsWith('/api') 
                                                                        ? `${process.env.REACT_APP_BACKEND_URL}${testimonial.image_url}`
                                                                        : testimonial.image_url
                                                                    }
                                                                    alt={testimonial.name}
                                                                    className="w-10 h-10 rounded-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                                                    <span className="text-primary-600 font-semibold">
                                                                        {testimonial.name?.charAt(0)?.toUpperCase() || '?'}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <p className="font-medium">{testimonial.name}</p>
                                                                <p className="text-sm text-neutral-500">{testimonial.role} • {testimonial.location}</p>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <p className="text-sm text-neutral-600 line-clamp-2 max-w-xs">
                                                            {testimonial.quote}
                                                        </p>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-0.5">
                                                            {[...Array(testimonial.rating || 5)].map((_, i) => (
                                                                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                                                            ))}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={testimonial.is_active ? 'default' : 'secondary'}>
                                                            {testimonial.is_active ? 'Active' : 'Hidden'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => {
                                                                    setEditingTestimonial(testimonial);
                                                                    setTestimonialFormData({
                                                                        name: testimonial.name,
                                                                        role: testimonial.role,
                                                                        location: testimonial.location,
                                                                        image_url: testimonial.image_url || '',
                                                                        quote: testimonial.quote,
                                                                        rating: testimonial.rating || 5,
                                                                        is_active: testimonial.is_active !== false,
                                                                        display_order: testimonial.display_order || 0,
                                                                    });
                                                                    setShowTestimonialDialog(true);
                                                                }}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="destructive"
                                                                onClick={() => handleDeleteTestimonial(testimonial.testimonial_id)}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {testimonials.length === 0 && (
                                        <div className="text-center py-12">
                                            <MessageSquareQuote className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                                            <p className="text-neutral-500">No testimonials yet. Add your first success story!</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>


                        {/* Paid Subscribers Tab */}
                        <TabsContent value="paid-subscribers">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-purple-600" />
                                        Paid Subscribers ({subscribersTotal})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <SearchFilterBar
                                        searchPlaceholder="Search subscribers by name or email..."
                                        onSearch={(val) => { setSubscribersSearch(val); fetchPaidSubscribers(1, val); }}
                                        filters={[]}
                                        filterValues={{}}
                                        onFilterChange={() => {}}
                                    />
                                    {paidSubscribers.length === 0 ? (
                                        <p className="text-center text-neutral-500 py-8">No paid subscribers found</p>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Subscription</TableHead>
                                                    <TableHead>Joined</TableHead>
                                                    <TableHead>Status</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {paidSubscribers.map((sub) => (
                                                    <TableRow key={sub.user_id} data-testid={`subscriber-row-${sub.user_id}`}>
                                                        <TableCell className="font-medium">{sub.name || 'N/A'}</TableCell>
                                                        <TableCell>{sub.email}</TableCell>
                                                        <TableCell>
                                                            <Badge className={sub.subscription_tier === 'pro' ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-blue-100 text-blue-800 border-blue-300'}>
                                                                {sub.subscription_tier === 'pro' ? 'Pro' : 'Standard'}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>{formatDate(sub.created_at)}</TableCell>
                                                        <TableCell>
                                                            <Badge className="bg-green-100 text-green-800 border-green-300">Active</Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                    <Pagination currentPage={subscribersPage} totalItems={subscribersTotal} onPageChange={(page) => fetchPaidSubscribers(page)} />
                                </CardContent>
                            </Card>
                        </TabsContent>


                        {/* Job Posts Tab */}
                        <TabsContent value="jobs">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <Briefcase className="w-5 h-5" />
                                        Job Posts ({jobsTotal})
                                    </CardTitle>
                                    <div className="flex gap-2">
                                        {selectedJobIds.length > 0 && (
                                            <Button
                                                variant="destructive"
                                                disabled={bulkDeleting}
                                                onClick={handleBulkDeleteJobs}
                                                data-testid="bulk-delete-jobs-btn"
                                            >
                                                {bulkDeleting ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                )}
                                                Delete Selected ({selectedJobIds.length})
                                            </Button>
                                        )}
                                        <Button 
                                            className="bg-primary-600 hover:bg-primary-700"
                                            onClick={() => navigate('/admin/jobs/new')}
                                            data-testid="add-job-btn"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Add Job Post
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <SearchFilterBar
                                        searchPlaceholder="Search jobs by title, agency or location..."
                                        onSearch={(val) => { setJobsSearch(val); fetchJobs(1, val); }}
                                        filters={[]}
                                        filterValues={{}}
                                        onFilterChange={() => {}}
                                    />
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-12">
                                                    <input
                                                        type="checkbox"
                                                        checked={jobPosts.length > 0 && selectedJobIds.length === jobPosts.length}
                                                        onChange={toggleAllAdminJobs}
                                                        className="cursor-pointer w-4 h-4 accent-primary-600"
                                                        data-testid="select-all-jobs-checkbox"
                                                        aria-label="Select all jobs"
                                                    />
                                                </TableHead>
                                                <TableHead>Job Title</TableHead>
                                                <TableHead>Agency</TableHead>
                                                <TableHead>Location</TableHead>
                                                <TableHead>Type</TableHead>
                                                <TableHead>Deadline</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {jobPosts.map((job) => (
                                                <TableRow key={job.job_id} className={selectedJobIds.includes(job.job_id) ? 'bg-primary-50/50' : ''}>
                                                    <TableCell>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedJobIds.includes(job.job_id)}
                                                            onChange={() => toggleAdminJobSelection(job.job_id)}
                                                            className="cursor-pointer w-4 h-4 accent-primary-600"
                                                            data-testid={`select-job-checkbox-${job.job_id}`}
                                                            aria-label={`Select ${job.title}`}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="font-medium">{job.title}</TableCell>
                                                    <TableCell>{job.agency_name}</TableCell>
                                                    <TableCell>{job.location}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="secondary">{job.job_type}</Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {job.application_deadline 
                                                            ? new Date(job.application_deadline).toLocaleDateString()
                                                            : '-'
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={job.status === 'active' ? 'default' : 'secondary'}>
                                                            {job.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant={job.status === 'active' ? 'outline' : 'default'}
                                                                className={job.status === 'active' ? 'text-red-600 hover:bg-red-50' : 'bg-emerald-600 hover:bg-emerald-700'}
                                                                data-testid={`toggle-job-status-${job.job_id}`}
                                                                onClick={async () => {
                                                                    const newStatus = job.status === 'active' ? 'inactive' : 'active';
                                                                    try {
                                                                        await adminJobsAPI.updateStatus(job.job_id, newStatus);
                                                                        toast.success(`Job set to ${newStatus}`);
                                                                        fetchJobs(jobsPage, jobsSearch);
                                                                    } catch (e) { toast.error('Failed to update status'); }
                                                                }}
                                                            >
                                                                {job.status === 'active' ? 'Deactivate' : 'Activate'}
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => navigate(`/admin/jobs/${job.job_id}/edit`)}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="destructive"
                                                                onClick={() => handleDeleteJob(job.job_id)}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {jobPosts.length === 0 && (
                                        <div className="text-center py-12">
                                            <Briefcase className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                                            <p className="text-neutral-500">No job posts yet. Create your first job listing!</p>
                                        </div>
                                    )}
                                    <Pagination currentPage={jobsPage} totalItems={jobsTotal} onPageChange={fetchJobs} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Job Scraper Tab */}
                        <TabsContent value="scraper">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Globe className="w-5 h-5" />
                                        Job Scraper Tool
                                    </CardTitle>
                                    <p className="text-sm text-neutral-500 mt-1">
                                        Scrape job listings from external websites and import them into the system
                                    </p>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* URL Input */}
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="text-sm font-medium mb-1 block">Website URL</label>
                                            <Input
                                                placeholder="https://example.com/jobs or https://example.com/job/123"
                                                value={scrapeUrl}
                                                onChange={(e) => setScrapeUrl(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleScrape()}
                                            />
                                            <p className="text-xs text-neutral-500 mt-1">
                                                Enter a job listing page URL or a single job page URL
                                            </p>
                                        </div>
                                        <div className="flex items-end">
                                            <Button
                                                onClick={handleScrape}
                                                disabled={scraping || !scrapeUrl.trim()}
                                                className="bg-primary-600 hover:bg-primary-700"
                                            >
                                                {scraping ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Scraping...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Search className="w-4 h-4 mr-2" />
                                                        Scrape Jobs
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Scraped Jobs Results */}
                                    {scrapedJobs.length > 0 && (
                                        <div className="border rounded-lg p-4 bg-neutral-50">
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="font-medium flex items-center gap-2">
                                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                                    Found {scrapedJobs.length} job(s)
                                                </h4>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={selectAllJobs}
                                                >
                                                    {selectedScrapedJobs.length === scrapedJobs.length ? 'Deselect All' : 'Select All'}
                                                </Button>
                                            </div>

                                            <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                                {scrapedJobs.map((job, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className={`p-3 border rounded-lg bg-white transition-colors ${
                                                            selectedScrapedJobs.includes(idx) 
                                                                ? 'border-primary-500 bg-primary-50' 
                                                                : 'hover:border-neutral-300'
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedScrapedJobs.includes(idx)}
                                                                onChange={() => toggleJobSelection(idx)}
                                                                className="mt-1 w-4 h-4 cursor-pointer"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <h5 className="font-medium text-neutral-900 truncate">
                                                                    {job.title}
                                                                </h5>
                                                                <div className="flex flex-wrap gap-2 mt-1 text-sm text-neutral-600">
                                                                    {job.company && (
                                                                        <span className="flex items-center gap-1">
                                                                            <Building2 className="w-3.5 h-3.5" />
                                                                            {job.company}
                                                                        </span>
                                                                    )}
                                                                    {job.location && (
                                                                        <span className="flex items-center gap-1">
                                                                            <MapPin className="w-3.5 h-3.5" />
                                                                            {job.location}
                                                                        </span>
                                                                    )}
                                                                    {job.salary && (
                                                                        <span className="text-emerald-600 font-medium">
                                                                            {job.salary}
                                                                        </span>
                                                                    )}
                                                                    {job.job_type && (
                                                                        <Badge variant="secondary" className="text-xs">
                                                                            {job.job_type}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                {job.description && (
                                                                    <p className="text-xs text-neutral-500 mt-1 line-clamp-2">
                                                                        {job.description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingScrapedJob({...job});
                                                                    setEditingScrapedJobIdx(idx);
                                                                }}
                                                                data-testid={`edit-scraped-job-${idx}`}
                                                            >
                                                                <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Import Section */}
                                            <div className="mt-4 pt-4 border-t flex flex-wrap items-end gap-4">
                                                <div className="flex-1 min-w-[200px]">
                                                    <label className="text-sm font-medium mb-1 block">
                                                        Assign to Agency (Optional)
                                                    </label>
                                                    <Select
                                                        value={importAgencyId}
                                                        onValueChange={setImportAgencyId}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="No agency - import as standalone jobs" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="none">No Agency (Standalone Jobs)</SelectItem>
                                                            {agencies.map((agency) => (
                                                                <SelectItem key={agency.agency_id} value={agency.agency_id}>
                                                                    {agency.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="w-full md:w-56">
                                                    <label className="text-sm font-medium mb-1 block">
                                                        Country *
                                                    </label>
                                                    <Select
                                                        value={importCountry}
                                                        onValueChange={setImportCountry}
                                                    >
                                                        <SelectTrigger data-testid="scraper-import-country-select">
                                                            <SelectValue placeholder="Select country" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                                                            <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <Button
                                                    onClick={handleImportJobs}
                                                    disabled={importing || selectedScrapedJobs.length === 0}
                                                    className="bg-emerald-600 hover:bg-emerald-700"
                                                    data-testid="scraper-import-jobs-btn"
                                                >
                                                    {importing ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Importing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Download className="w-4 h-4 mr-2" />
                                                            Import {selectedScrapedJobs.length} Job(s)
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Edit Scraped Job Dialog */}
                                    {editingScrapedJob && (
                                        <Dialog open={!!editingScrapedJob} onOpenChange={() => { setEditingScrapedJob(null); setEditingScrapedJobIdx(null); }}>
                                            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                                                <DialogHeader>
                                                    <DialogTitle>Edit Job Before Import</DialogTitle>
                                                </DialogHeader>
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="text-sm font-medium">Title *</label>
                                                            <Input value={editingScrapedJob.title || ''} onChange={(e) => setEditingScrapedJob({...editingScrapedJob, title: e.target.value})} />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Company</label>
                                                            <Input value={editingScrapedJob.company || ''} onChange={(e) => setEditingScrapedJob({...editingScrapedJob, company: e.target.value})} />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Location</label>
                                                            <Input value={editingScrapedJob.location || ''} onChange={(e) => setEditingScrapedJob({...editingScrapedJob, location: e.target.value})} />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Salary</label>
                                                            <Input value={editingScrapedJob.salary || ''} onChange={(e) => setEditingScrapedJob({...editingScrapedJob, salary: e.target.value})} />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Job Type</label>
                                                            <Select value={editingScrapedJob.job_type || ''} onValueChange={(v) => setEditingScrapedJob({...editingScrapedJob, job_type: v})}>
                                                                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                                                <SelectContent>
                                                                    {['full-time','part-time','contract','temporary','internship','volunteer'].map(t => (
                                                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Work Mode</label>
                                                            <Select value={editingScrapedJob.work_mode || ''} onValueChange={(v) => setEditingScrapedJob({...editingScrapedJob, work_mode: v})}>
                                                                <SelectTrigger><SelectValue placeholder="Select mode" /></SelectTrigger>
                                                                <SelectContent>
                                                                    {['on-site','remote','hybrid'].map(t => (
                                                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Expiry Date</label>
                                                            <Input 
                                                                type="date" 
                                                                value={editingScrapedJob.expires_at || ''} 
                                                                onChange={(e) => setEditingScrapedJob({...editingScrapedJob, expires_at: e.target.value})}
                                                                min={new Date().toISOString().split('T')[0]}
                                                            />
                                                            <p className="text-xs text-neutral-500 mt-1">Job will be hidden after this date</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Country</label>
                                                            <Select
                                                                value={editingScrapedJob.country || ''}
                                                                onValueChange={(v) => setEditingScrapedJob({...editingScrapedJob, country: v})}
                                                            >
                                                                <SelectTrigger data-testid="edit-scraped-job-country-select">
                                                                    <SelectValue placeholder="Use bulk country" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                                                                    <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            <p className="text-xs text-neutral-500 mt-1">Overrides the bulk country for this job only</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Apply URL</label>
                                                        <Input value={editingScrapedJob.apply_url || ''} onChange={(e) => setEditingScrapedJob({...editingScrapedJob, apply_url: e.target.value})} />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Description</label>
                                                        <Textarea rows={8} value={editingScrapedJob.description || ''} onChange={(e) => setEditingScrapedJob({...editingScrapedJob, description: e.target.value})} />
                                                    </div>
                                                    <div className="flex justify-end gap-3">
                                                        <Button variant="outline" onClick={() => { setEditingScrapedJob(null); setEditingScrapedJobIdx(null); }}>Cancel</Button>
                                                        <Button onClick={handleSaveScrapedJob} data-testid="save-scraped-job-btn">Save Changes</Button>
                                                    </div>
                                                </div>
                                            </DialogContent>
                                        </Dialog>
                                    )}


                                    {/* Tips */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <h5 className="font-medium text-blue-900 flex items-center gap-2 mb-2">
                                            <AlertCircle className="w-4 h-4" />
                                            Tips for Better Results
                                        </h5>
                                        <ul className="text-sm text-blue-800 space-y-1">
                                            <li>• Use URLs from job listing pages (e.g., search results, category pages)</li>
                                            <li>• You can also scrape individual job pages for detailed information</li>
                                            <li>• Works best with popular job sites like Indeed, Reed, TotalJobs, etc.</li>
                                            <li>• Some websites may block scraping - try different sources if needed</li>
                                        </ul>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Multi-Apply pop-up stats */}
                        <TabsContent value="upsell-stats">
                            <UpsellStatsPanel />
                        </TabsContent>

                        {/* Auto-Sync (Job Sources) Tab */}
                        <TabsContent value="job-sources">
                            <JobSourcesPanel />
                        </TabsContent>

                        {/* Agency Scraper Tab */}
                        <TabsContent value="agency-scraper">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Building2 className="w-5 h-5" />
                                        Agency Scraper Tool
                                    </CardTitle>
                                    <p className="text-sm text-neutral-500 mt-1">
                                        Scrape recruitment agency information from external websites and import them into the system
                                    </p>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* URL Input */}
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="text-sm font-medium mb-1 block">Website URL</label>
                                            <Input
                                                placeholder="https://example.com/recruitment-agencies or https://example.com/agency/abc"
                                                value={agencyScrapeUrl}
                                                onChange={(e) => setAgencyScrapeUrl(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleAgencyScrape()}
                                            />
                                            <p className="text-xs text-neutral-500 mt-1">
                                                Enter a directory page URL or a single agency page URL
                                            </p>
                                        </div>
                                        <div className="flex items-end">
                                            <Button
                                                onClick={handleAgencyScrape}
                                                disabled={agencyScraping || !agencyScrapeUrl.trim()}
                                                className="bg-primary-600 hover:bg-primary-700"
                                            >
                                                {agencyScraping ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Scraping...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Search className="w-4 h-4 mr-2" />
                                                        Scrape Agencies
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Scraped Agencies Results */}
                                    {scrapedAgencies.length > 0 && (
                                        <div className="border rounded-lg p-4 bg-neutral-50">
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="font-medium flex items-center gap-2">
                                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                                    Found {scrapedAgencies.length} agency/agencies
                                                </h4>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={selectAllAgencies}
                                                >
                                                    {selectedScrapedAgencies.length === scrapedAgencies.length ? 'Deselect All' : 'Select All'}
                                                </Button>
                                            </div>

                                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                                {scrapedAgencies.map((agency, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className={`p-4 border rounded-lg bg-white transition-colors ${
                                                            selectedScrapedAgencies.includes(idx) 
                                                                ? 'border-primary-500 bg-primary-50' 
                                                                : 'hover:border-neutral-300'
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedScrapedAgencies.includes(idx)}
                                                                onChange={() => toggleAgencySelection(idx)}
                                                                className="mt-1 w-4 h-4"
                                                            />
                                                            <div className="flex-1 min-w-0 space-y-3">
                                                                {/* Name */}
                                                                <div>
                                                                    <label className="text-xs font-medium text-neutral-500">Agency Name</label>
                                                                    <Input
                                                                        value={agency.name || ''}
                                                                        onChange={(e) => updateScrapedAgency(idx, 'name', e.target.value)}
                                                                        className="mt-1"
                                                                    />
                                                                </div>
                                                                
                                                                <div className="grid grid-cols-2 gap-3">
                                                                    {/* City */}
                                                                    <div>
                                                                        <label className="text-xs font-medium text-neutral-500">City</label>
                                                                        <Select
                                                                            value={agency.city || ''}
                                                                            onValueChange={(value) => updateScrapedAgency(idx, 'city', value)}
                                                                        >
                                                                            <SelectTrigger className="mt-1">
                                                                                <SelectValue placeholder="Select city" />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                {UK_CITIES.map((city) => (
                                                                                    <SelectItem key={city} value={city}>{city}</SelectItem>
                                                                                ))}
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                    
                                                                    {/* Postcode */}
                                                                    <div>
                                                                        <label className="text-xs font-medium text-neutral-500">Postcode</label>
                                                                        <Input
                                                                            value={agency.postcode || ''}
                                                                            onChange={(e) => updateScrapedAgency(idx, 'postcode', e.target.value)}
                                                                            placeholder="e.g., EC1A 1BB"
                                                                            className="mt-1"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="grid grid-cols-2 gap-3">
                                                                    {/* Phone */}
                                                                    <div>
                                                                        <label className="text-xs font-medium text-neutral-500">Phone</label>
                                                                        <Input
                                                                            value={agency.phone || ''}
                                                                            onChange={(e) => updateScrapedAgency(idx, 'phone', e.target.value)}
                                                                            className="mt-1"
                                                                        />
                                                                    </div>
                                                                    
                                                                    {/* Email */}
                                                                    <div>
                                                                        <label className="text-xs font-medium text-neutral-500">Email</label>
                                                                        <Input
                                                                            value={agency.email || ''}
                                                                            onChange={(e) => updateScrapedAgency(idx, 'email', e.target.value)}
                                                                            className="mt-1"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Website */}
                                                                <div>
                                                                    <label className="text-xs font-medium text-neutral-500">Website</label>
                                                                    <Input
                                                                        value={agency.website || ''}
                                                                        onChange={(e) => updateScrapedAgency(idx, 'website', e.target.value)}
                                                                        className="mt-1"
                                                                    />
                                                                </div>
                                                                
                                                                {/* Address */}
                                                                <div>
                                                                    <label className="text-xs font-medium text-neutral-500">Address</label>
                                                                    <Input
                                                                        value={agency.address || ''}
                                                                        onChange={(e) => updateScrapedAgency(idx, 'address', e.target.value)}
                                                                        className="mt-1"
                                                                    />
                                                                </div>
                                                                
                                                                {/* Industry */}
                                                                <div>
                                                                    <label className="text-xs font-medium text-neutral-500">Industries</label>
                                                                    <Select
                                                                        value={agency.industry?.[0] || ''}
                                                                        onValueChange={(value) => updateScrapedAgency(idx, 'industry', [value])}
                                                                    >
                                                                        <SelectTrigger className="mt-1">
                                                                            <SelectValue placeholder="Select industry" />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            {INDUSTRIES.map((ind) => (
                                                                                <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                </div>
                                                                
                                                                {/* Description */}
                                                                {agency.description && (
                                                                    <div>
                                                                        <label className="text-xs font-medium text-neutral-500">Description</label>
                                                                        <p className="text-xs text-neutral-600 mt-1 line-clamp-3 bg-neutral-50 p-2 rounded">
                                                                            {agency.description}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Import Button */}
                                            <div className="mt-4 pt-4 border-t flex justify-end">
                                                <Button
                                                    onClick={handleImportAgencies}
                                                    disabled={agencyImporting || selectedScrapedAgencies.length === 0}
                                                    className="bg-emerald-600 hover:bg-emerald-700"
                                                >
                                                    {agencyImporting ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Importing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Download className="w-4 h-4 mr-2" />
                                                            Import {selectedScrapedAgencies.length} Agency/Agencies
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Tips */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <h5 className="font-medium text-blue-900 flex items-center gap-2 mb-2">
                                            <AlertCircle className="w-4 h-4" />
                                            Tips for Better Results
                                        </h5>
                                        <ul className="text-sm text-blue-800 space-y-1">
                                            <li>• Use URLs from recruitment agency directories (e.g., Google Maps listings, Yelp, business directories)</li>
                                            <li>• You can also scrape individual agency websites for detailed information</li>
                                            <li>• Review and edit the scraped data before importing - especially city and industry</li>
                                            <li>• Some websites may block scraping - try different sources if needed</li>
                                            <li>• Make sure to select a city for proper map placement</li>
                                        </ul>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Admin Management Tab (Super Admin Only) */}
                        {isSuperAdmin && (
                            <TabsContent value="admin-management">
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle className="flex items-center gap-2">
                                                    <Shield className="w-5 h-5 text-amber-500" />
                                                    Admin Management
                                                </CardTitle>
                                                <CardDescription>
                                                    Manage admin accounts - create, edit, or remove administrators
                                                </CardDescription>
                                            </div>
                                            <Dialog open={showCreateAdminDialog} onOpenChange={setShowCreateAdminDialog}>
                                                <DialogTrigger asChild>
                                                    <Button className="bg-primary-600 hover:bg-primary-700" data-testid="create-admin-btn">
                                                        <Plus className="w-4 h-4 mr-2" />
                                                        Create Admin
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent>
                                                    <DialogHeader>
                                                        <DialogTitle>Create New Admin</DialogTitle>
                                                        <DialogDescription>
                                                            Create a new administrator account
                                                        </DialogDescription>
                                                    </DialogHeader>
                                                    <form onSubmit={handleCreateAdmin} className="space-y-4">
                                                        <div>
                                                            <label className="text-sm font-medium">Name *</label>
                                                            <Input
                                                                required
                                                                value={createAdminForm.name}
                                                                onChange={(e) => setCreateAdminForm({...createAdminForm, name: e.target.value})}
                                                                placeholder="Admin Name"
                                                                data-testid="create-admin-name"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Email *</label>
                                                            <Input
                                                                required
                                                                type="email"
                                                                value={createAdminForm.email}
                                                                onChange={(e) => setCreateAdminForm({...createAdminForm, email: e.target.value})}
                                                                placeholder="admin@example.com"
                                                                data-testid="create-admin-email"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium">Password *</label>
                                                            <Input
                                                                required
                                                                type="password"
                                                                value={createAdminForm.password}
                                                                onChange={(e) => setCreateAdminForm({...createAdminForm, password: e.target.value})}
                                                                placeholder="Min 8 characters"
                                                                data-testid="create-admin-password"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-medium block mb-2">Permissions</label>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                {[
                                                                    { id: 'agencies', label: 'Agencies' },
                                                                    { id: 'jobs', label: 'Job Posts' },
                                                                    { id: 'multi-apply', label: 'Multi-Apply Orders' },
                                                                    { id: 'cv-reviews', label: 'CV Reviews' },
                                                                    { id: 'consultations', label: 'Consultations' },
                                                                    { id: 'blog', label: 'Blog' },
                                                                    { id: 'testimonials', label: 'Testimonials' },
                                                                    { id: 'notifications', label: 'Notifications' },
                                                                ].map(perm => (
                                                                    <label key={perm.id} className="flex items-center gap-2 text-sm p-2 border rounded hover:bg-neutral-50 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={createAdminForm.permissions.includes(perm.id)}
                                                                            onChange={(e) => {
                                                                                const perms = e.target.checked
                                                                                    ? [...createAdminForm.permissions, perm.id]
                                                                                    : createAdminForm.permissions.filter(p => p !== perm.id);
                                                                                setCreateAdminForm({...createAdminForm, permissions: perms});
                                                                            }}
                                                                            className="w-4 h-4"
                                                                            data-testid={`perm-${perm.id}`}
                                                                        />
                                                                        {perm.label}
                                                                    </label>
                                                                ))}
                                                            </div>
                                                            <p className="text-xs text-neutral-500 mt-1">Leave empty for full access</p>
                                                        </div>
                                                        <DialogFooter>
                                                            <Button type="button" variant="outline" onClick={() => setShowCreateAdminDialog(false)}>
                                                                Cancel
                                                            </Button>
                                                            <Button type="submit" className="bg-primary-600 hover:bg-primary-700">
                                                                Create Admin
                                                            </Button>
                                                        </DialogFooter>
                                                    </form>
                                                </DialogContent>
                                            </Dialog>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {adminUsersLoading ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                                            </div>
                                        ) : adminUsers.length === 0 ? (
                                            <div className="text-center py-12 text-neutral-500">
                                                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                                <p>No admin users found</p>
                                                <Button 
                                                    className="mt-4" 
                                                    variant="outline"
                                                    onClick={fetchAdminUsers}
                                                >
                                                    Refresh
                                                </Button>
                                            </div>
                                        ) : (
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Name</TableHead>
                                                        <TableHead>Email</TableHead>
                                                        <TableHead>Permissions</TableHead>
                                                        <TableHead>Created</TableHead>
                                                        <TableHead>Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {adminUsers.map((admin) => (
                                                        <TableRow key={admin.user_id} data-testid={`admin-row-${admin.user_id}`}>
                                                            <TableCell className="font-medium">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                                                        <User className="w-4 h-4 text-primary-600" />
                                                                    </div>
                                                                    {admin.name}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>{admin.email}</TableCell>
                                                            <TableCell>
                                                                {admin.permissions?.length > 0 ? (
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {admin.permissions.map(p => (
                                                                            <Badge key={p} variant="outline" className="text-xs capitalize">{p}</Badge>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">Full Access</Badge>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-sm text-neutral-500">
                                                                {formatDate(admin.created_at)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="flex gap-2">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => handleEditAdmin(admin)}
                                                                        data-testid={`edit-admin-${admin.user_id}`}
                                                                    >
                                                                        <Edit2 className="w-4 h-4" />
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="destructive"
                                                                        onClick={() => handleDeleteAdmin(admin.user_id, admin.email)}
                                                                        disabled={admin.user_id === user?.user_id}
                                                                        title={admin.user_id === user?.user_id ? "Cannot delete your own account" : "Delete admin"}
                                                                        data-testid={`delete-admin-${admin.user_id}`}
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Edit Admin Dialog */}
                                <Dialog open={showEditAdminDialog} onOpenChange={setShowEditAdminDialog}>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Edit Admin</DialogTitle>
                                            <DialogDescription>
                                                Update credentials for {editingAdmin?.name}
                                            </DialogDescription>
                                        </DialogHeader>
                                        <form onSubmit={handleUpdateAdmin} className="space-y-4">
                                            <div>
                                                <label className="text-sm font-medium">Name</label>
                                                <Input
                                                    value={editAdminForm.name}
                                                    onChange={(e) => setEditAdminForm({...editAdminForm, name: e.target.value})}
                                                    placeholder="Admin Name"
                                                    data-testid="edit-admin-name"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">Email</label>
                                                <Input
                                                    type="email"
                                                    value={editAdminForm.email}
                                                    onChange={(e) => setEditAdminForm({...editAdminForm, email: e.target.value})}
                                                    placeholder="admin@example.com"
                                                    data-testid="edit-admin-email"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">New Password (leave blank to keep current)</label>
                                                <Input
                                                    type="password"
                                                    value={editAdminForm.password}
                                                    onChange={(e) => setEditAdminForm({...editAdminForm, password: e.target.value})}
                                                    placeholder="Min 8 characters"
                                                    data-testid="edit-admin-password"
                                                />
                                            </div>
                                            <DialogFooter>
                                                <Button type="button" variant="outline" onClick={() => {
                                                    setShowEditAdminDialog(false);
                                                    setEditingAdmin(null);
                                                    setEditAdminForm({ email: '', name: '', password: '' });
                                                }}>
                                                    Cancel
                                                </Button>
                                                <Button type="submit" className="bg-primary-600 hover:bg-primary-700">
                                                    Save Changes
                                                </Button>
                                            </DialogFooter>
                                        </form>
                                    </DialogContent>
                                </Dialog>
                            </TabsContent>
                        )}

                        {/* Staff Management Tab (Super Admin Only) */}
                        {isSuperAdmin && (
                            <TabsContent value="staff-management">
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle className="flex items-center gap-2">
                                                    <Users className="w-5 h-5 text-primary-600" />
                                                    Staff Accounts
                                                </CardTitle>
                                                <CardDescription>
                                                    Staff members can only access the Multi-Apply queue to update application progress.
                                                </CardDescription>
                                            </div>
                                            <Button className="bg-primary-600 hover:bg-primary-700" onClick={() => setShowCreateStaffDialog(true)} data-testid="create-staff-btn">
                                                <Plus className="w-4 h-4 mr-2" /> Add Staff
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {staffLoading ? (
                                            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>
                                        ) : staffUsers.length === 0 ? (
                                            <div className="text-center py-12 text-neutral-500">
                                                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                                <p>No staff accounts yet</p>
                                                <p className="text-xs mt-1">Add a staff member to help process multi-apply orders.</p>
                                            </div>
                                        ) : (
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Name</TableHead>
                                                        <TableHead>Email</TableHead>
                                                        <TableHead>Created</TableHead>
                                                        <TableHead>Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {staffUsers.map((s) => (
                                                        <TableRow key={s.user_id} data-testid={`staff-row-${s.user_id}`}>
                                                            <TableCell className="font-medium">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                                                        <User className="w-4 h-4 text-emerald-600" />
                                                                    </div>
                                                                    {s.name}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>{s.email}</TableCell>
                                                            <TableCell className="text-sm text-neutral-500">{formatDate(s.created_at)}</TableCell>
                                                            <TableCell>
                                                                <div className="flex gap-2">
                                                                    <Button size="sm" variant="outline" onClick={() => handleEditStaff(s)} data-testid={`edit-staff-${s.user_id}`}>
                                                                        <Edit2 className="w-4 h-4" />
                                                                    </Button>
                                                                    <Button size="sm" variant="destructive" onClick={() => handleDeleteStaff(s.user_id, s.email)} data-testid={`delete-staff-${s.user_id}`}>
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Create Staff Dialog */}
                                <Dialog open={showCreateStaffDialog} onOpenChange={setShowCreateStaffDialog}>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Create Staff Account</DialogTitle>
                                            <DialogDescription>Staff members access only the Multi-Apply queue.</DialogDescription>
                                        </DialogHeader>
                                        <form onSubmit={handleCreateStaff} className="space-y-4">
                                            <div>
                                                <label className="text-sm font-medium">Name *</label>
                                                <Input required value={createStaffForm.name} onChange={(e) => setCreateStaffForm({ ...createStaffForm, name: e.target.value })} placeholder="Staff Name" data-testid="create-staff-name" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">Email *</label>
                                                <Input required type="email" value={createStaffForm.email} onChange={(e) => setCreateStaffForm({ ...createStaffForm, email: e.target.value })} placeholder="staff@example.com" data-testid="create-staff-email" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">Password *</label>
                                                <Input required type="password" value={createStaffForm.password} onChange={(e) => setCreateStaffForm({ ...createStaffForm, password: e.target.value })} placeholder="Min 8 characters" data-testid="create-staff-password" />
                                            </div>
                                            <DialogFooter>
                                                <Button type="button" variant="outline" onClick={() => setShowCreateStaffDialog(false)}>Cancel</Button>
                                                <Button type="submit" className="bg-primary-600 hover:bg-primary-700">Create Staff</Button>
                                            </DialogFooter>
                                        </form>
                                    </DialogContent>
                                </Dialog>

                                {/* Edit Staff Dialog */}
                                <Dialog open={showEditStaffDialog} onOpenChange={setShowEditStaffDialog}>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Edit Staff</DialogTitle>
                                            <DialogDescription>Update credentials for {editingStaff?.name}</DialogDescription>
                                        </DialogHeader>
                                        <form onSubmit={handleUpdateStaff} className="space-y-4">
                                            <div>
                                                <label className="text-sm font-medium">Name</label>
                                                <Input value={editStaffForm.name} onChange={(e) => setEditStaffForm({ ...editStaffForm, name: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">Email</label>
                                                <Input type="email" value={editStaffForm.email} onChange={(e) => setEditStaffForm({ ...editStaffForm, email: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium">New Password (leave blank to keep current)</label>
                                                <Input type="password" value={editStaffForm.password} onChange={(e) => setEditStaffForm({ ...editStaffForm, password: e.target.value })} placeholder="Min 8 characters" />
                                            </div>
                                            <DialogFooter>
                                                <Button type="button" variant="outline" onClick={() => { setShowEditStaffDialog(false); setEditingStaff(null); }}>Cancel</Button>
                                                <Button type="submit" className="bg-primary-600 hover:bg-primary-700">Save</Button>
                                            </DialogFooter>
                                        </form>
                                    </DialogContent>
                                </Dialog>
                            </TabsContent>
                        )}

                        {/* Site Settings Tab (Super Admin Only) */}
                        {isSuperAdmin && (
                            <TabsContent value="site-settings">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Site Settings</CardTitle>
                                        <CardDescription>Global site configuration.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4 max-w-xl">
                                        <div>
                                            <label className="text-sm font-medium">WhatsApp number (with country code)</label>
                                            <Input
                                                value={siteSettings.whatsapp_number}
                                                onChange={(e) => setSiteSettings({ ...siteSettings, whatsapp_number: e.target.value })}
                                                placeholder="+441234567890"
                                                data-testid="whatsapp-number-input"
                                            />
                                            <p className="text-xs text-neutral-500 mt-1">Used for the "Contact us on WhatsApp" button on practical courses.</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Display label (optional)</label>
                                            <Input
                                                value={siteSettings.whatsapp_display}
                                                onChange={(e) => setSiteSettings({ ...siteSettings, whatsapp_display: e.target.value })}
                                                placeholder="+44 1234 567890"
                                                data-testid="whatsapp-display-input"
                                            />
                                        </div>
                                        <Button onClick={handleSaveWhatsAppSettings} disabled={savingSettings} className="bg-primary-600 hover:bg-primary-700" data-testid="save-whatsapp-btn">
                                            {savingSettings ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save WhatsApp Settings'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        )}

                        {/* Course Inquiries Tab */}
                        <TabsContent value="course-inquiries">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Course Inquiries ({courseInquiries.length})</CardTitle>
                                    <CardDescription>Enquiries submitted from the Practical Courses pages.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {courseInquiriesLoading ? (
                                        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>
                                    ) : courseInquiries.length === 0 ? (
                                        <div className="text-center py-12 text-neutral-500">
                                            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                            <p>No inquiries yet</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Course</TableHead>
                                                    <TableHead>Contact</TableHead>
                                                    <TableHead>Message</TableHead>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead>Status</TableHead>
                                                    <TableHead>Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {courseInquiries.map((inq) => (
                                                    <TableRow key={inq.inquiry_id} data-testid={`inquiry-row-${inq.inquiry_id}`}>
                                                        <TableCell className="font-medium text-sm">{inq.course_title || '—'}</TableCell>
                                                        <TableCell>
                                                            <div className="text-sm">
                                                                <div className="font-medium">{inq.name}</div>
                                                                <div className="text-xs text-neutral-500">{inq.email}</div>
                                                                <div className="text-xs text-neutral-500">{inq.phone}</div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-xs text-neutral-600 max-w-[220px] truncate" title={inq.message}>{inq.message || '—'}</TableCell>
                                                        <TableCell className="text-xs text-neutral-500">{formatDate(inq.created_at)}</TableCell>
                                                        <TableCell>
                                                            <Select value={inq.status || 'new'} onValueChange={(v) => handleUpdateInquiryStatus(inq.inquiry_id, v)}>
                                                                <SelectTrigger className="h-8 w-32" data-testid={`inquiry-status-${inq.inquiry_id}`}>
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="new">New</SelectItem>
                                                                    <SelectItem value="contacted">Contacted</SelectItem>
                                                                    <SelectItem value="enrolled">Enrolled</SelectItem>
                                                                    <SelectItem value="closed">Closed</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button size="sm" variant="destructive" onClick={() => handleDeleteInquiry(inq.inquiry_id)} data-testid={`delete-inquiry-${inq.inquiry_id}`}>
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Notifications Tab */}
                        <TabsContent value="notifications">
                            <AdminNotificationsSection />
                        </TabsContent>

                    </Tabs>

            {/* Multi-Apply Order Details Dialog */}
            <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Multi-Apply Order Details</DialogTitle>
                        <DialogDescription>
                            Order ID: {selectedOrder?.order_id}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedOrder && (
                        <div className="space-y-6">
                            {/* User Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-neutral-500">User</label>
                                    <p className="font-medium">{selectedOrder.user_name || 'N/A'}</p>
                                    <p className="text-sm text-neutral-500">{selectedOrder.user_email}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-neutral-500">Plan</label>
                                    <p className="font-medium">{selectedOrder.package?.toUpperCase()}</p>
                                </div>
                            </div>

                            {/* Job Preferences */}
                            <div className="border rounded-lg p-4 space-y-4">
                                <h4 className="font-semibold text-lg border-b pb-2">Job Preferences</h4>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-neutral-500">Employment Status</label>
                                        <p>{selectedOrder.job_preferences?.employment_status || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-neutral-500">Job Title</label>
                                        <p>{selectedOrder.job_preferences?.job_titles?.join(', ') || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-neutral-500">Experience Level</label>
                                        <p>{selectedOrder.job_preferences?.experience_level || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-neutral-500">Salary Range</label>
                                        <p>{typeof selectedOrder.job_preferences?.salary_range === 'object' 
                                            ? `£${selectedOrder.job_preferences?.salary_range?.min || 0}+` 
                                            : selectedOrder.job_preferences?.salary_range || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-neutral-500">Start Date</label>
                                        <p>{selectedOrder.job_preferences?.start_asap ? 'ASAP' : selectedOrder.job_preferences?.start_date || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-neutral-500">Willing to Relocate</label>
                                        <p>{selectedOrder.job_preferences?.willing_to_relocate ? 'Yes' : 'No'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-neutral-500">Driver's License</label>
                                        <p>{selectedOrder.job_preferences?.has_driver_license || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-neutral-500">Country</label>
                                        <p>{selectedOrder.job_preferences?.country || 'N/A'}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-neutral-500">Work Types</label>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {selectedOrder.job_preferences?.work_types?.map((type) => (
                                            <Badge key={type} variant="outline">{type}</Badge>
                                        )) || <span className="text-neutral-400">N/A</span>}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-neutral-500">Work Location</label>
                                    <div className="flex gap-2 mt-1">
                                        {selectedOrder.job_preferences?.work_location_remote && <Badge>Remote</Badge>}
                                        {selectedOrder.job_preferences?.work_location_hybrid && <Badge>In-person/Hybrid</Badge>}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-neutral-500">Preferred Cities</label>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {selectedOrder.job_preferences?.cities?.length > 0 
                                            ? selectedOrder.job_preferences.cities.map((city) => (
                                                <Badge key={city} variant="secondary">{city}</Badge>
                                            ))
                                            : <span className="text-neutral-400">Any</span>
                                        }
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-neutral-500">Industries</label>
                                    <div className="flex flex-wrap gap-1 mt-1 max-h-24 overflow-y-auto">
                                        {selectedOrder.job_preferences?.industries?.length > 0 
                                            ? selectedOrder.job_preferences.industries.map((ind) => (
                                                <Badge key={ind} variant="outline" className="text-xs">{ind}</Badge>
                                            ))
                                            : <span className="text-neutral-400">Any</span>
                                        }
                                    </div>
                                </div>
                            </div>

                            {/* Status Update */}
                            <div className="border rounded-lg p-4">
                                <label className="text-sm font-medium text-neutral-500 block mb-2">Update Status</label>
                                <div className="flex gap-2">
                                    {['pending', 'in_progress', 'completed', 'cancelled'].map((status) => (
                                        <Button
                                            key={status}
                                            variant={selectedOrder.status === status ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={async () => {
                                                try {
                                                    await adminAgencyAPI.updateMultiApplyStatus(selectedOrder.order_id, status);
                                                    toast.success('Status updated');
                                                    setSelectedOrder({ ...selectedOrder, status });
                                                    fetchData();
                                                } catch (error) {
                                                    toast.error('Failed to update status');
                                                }
                                            }}
                                        >
                                            {status.replace('_', ' ')}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {/* Update Jobs Applied */}
                            <div className="border rounded-lg p-4">
                                <label className="text-sm font-medium text-neutral-500 block mb-2">Update Jobs Applied</label>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="text-sm">
                                        <span className="text-neutral-500">Total Credits: </span>
                                        <span className="font-bold">{selectedOrder.applications_count || selectedOrder.applications_total || 0}</span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-neutral-500">Currently Applied: </span>
                                        <span className="font-bold">{selectedOrder.applications_sent || 0}</span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-neutral-500">Remaining: </span>
                                        <span className="font-bold">{(selectedOrder.applications_count || selectedOrder.applications_total || 0) - (selectedOrder.applications_sent || 0)}</span>
                                    </div>
                                </div>
                                <div className="flex items-end gap-3">
                                    <div className="flex-1">
                                        <label className="text-xs text-neutral-500 block mb-1">Jobs Applied</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max={selectedOrder.applications_count || selectedOrder.applications_total || 999}
                                            defaultValue={selectedOrder.applications_sent || 0}
                                            id="credit-input"
                                            className="w-full px-3 py-2 border rounded-md text-sm"
                                            data-testid="credit-input"
                                        />
                                    </div>
                                    <Button
                                        size="sm"
                                        data-testid="update-credits-btn"
                                        onClick={async () => {
                                            const val = parseInt(document.getElementById('credit-input').value);
                                            if (isNaN(val) || val < 0) {
                                                toast.error('Enter a valid number'); return;
                                            }
                                            try {
                                                const res = await adminAgencyAPI.updateMultiApplyCredits(selectedOrder.order_id, { applications_sent: val });
                                                toast.success('Credits updated successfully');
                                                setSelectedOrder({
                                                    ...selectedOrder,
                                                    applications_sent: res.data.applications_sent,
                                                    applications_remaining: res.data.applications_remaining,
                                                });
                                                fetchData();
                                            } catch (error) {
                                                toast.error(error.response?.data?.detail || 'Failed to update credits');
                                            }
                                        }}
                                    >
                                        Update Credits
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedOrder(null)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Export Agencies Dialog (with country selection) */}
            <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
                <DialogContent className="max-w-md" data-testid="export-dialog">
                    <DialogHeader>
                        <DialogTitle>Export Agencies</DialogTitle>
                        <DialogDescription>
                            Choose the country and format for the export.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Country</label>
                            <Select value={exportCountry} onValueChange={setExportCountry}>
                                <SelectTrigger data-testid="export-country-select">
                                    <SelectValue placeholder="Select country" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">🌍 All Countries</SelectItem>
                                    <SelectItem value="UK">🇬🇧 United Kingdom only</SelectItem>
                                    <SelectItem value="Canada">🇨🇦 Canada only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <label className="text-sm font-medium mb-1 block">Format</label>
                            <Select value={exportFormat} onValueChange={setExportFormat}>
                                <SelectTrigger data-testid="export-format-select">
                                    <SelectValue placeholder="Select format" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="excel">📊 Excel (.xlsx)</SelectItem>
                                    <SelectItem value="pdf">📄 PDF</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowExportDialog(false)}
                            disabled={exporting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleExportAgencies(exportFormat, exportCountry)}
                            disabled={exporting}
                            className="bg-primary-600 hover:bg-primary-700"
                            data-testid="export-download-btn"
                        >
                            {exporting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <FileDown className="w-4 h-4 mr-2" />
                                    Download
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Import Agencies Dialog (with country selection) */}
            <Dialog open={showBulkImportDialog} onOpenChange={(open) => {
                setShowBulkImportDialog(open);
                if (!open) setBulkImportFile(null);
            }}>
                <DialogContent className="max-w-md" data-testid="bulk-import-dialog">
                    <DialogHeader>
                        <DialogTitle>Bulk Import Agencies</DialogTitle>
                        <DialogDescription>
                            Select the country these agencies belong to, then choose the JSON file to upload.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Country *</label>
                            <Select value={bulkImportCountry} onValueChange={setBulkImportCountry}>
                                <SelectTrigger data-testid="bulk-import-country-select">
                                    <SelectValue placeholder="Select country" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                                    <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-neutral-500 mt-1">
                                All imported agencies (without an explicit country in the file) will be tagged with this country.
                            </p>
                        </div>

                        <div>
                            <label className="text-sm font-medium mb-1 block">JSON File *</label>
                            <input
                                type="file"
                                accept=".json"
                                onChange={handleBulkImportFileSelect}
                                className="block w-full text-sm text-neutral-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                                disabled={bulkImporting}
                                data-testid="bulk-import-file-input"
                            />
                            {bulkImportFile && (
                                <p className="text-xs text-emerald-600 mt-1">
                                    Selected: <strong>{bulkImportFile.name}</strong> ({(bulkImportFile.size / 1024).toFixed(1)} KB)
                                </p>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowBulkImportDialog(false);
                                setBulkImportFile(null);
                            }}
                            disabled={bulkImporting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleBulkImport}
                            disabled={bulkImporting || !bulkImportFile || !bulkImportCountry}
                            className="bg-primary-600 hover:bg-primary-700"
                            data-testid="bulk-import-upload-btn"
                        >
                            {bulkImporting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AdminDashboard;
